import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Rating,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  PictureAsPdf as PdfIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  CompareArrows as CompareArrowsIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Star as StarIcon,
  SmartDisplay as SmartDisplayIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import FloatingVideoPlayer from '@components/FloatingVideoPlayer';
import SideBySideVideoPlayer from '@components/SideBySideVideoPlayer';
import { useAuthStore } from '@store/authStore';
import { generateRankingEvaluationReportPDF } from '@utils/generateRankingEvaluationReportPDF';
import { generateRankingEvaluationManualForm } from '@utils/generateRankingEvaluationManualForm';
import { normalizeStrokeVideoSections } from '@utils/strokeVideoSections';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import type { EvaluatorScoreMetrics } from '@utils/rankingEvaluationNav';

export interface RankingEvaluationEmbeddedState {
  metrics: EvaluatorScoreMetrics;
  canRate: boolean;
  canDownloadManual: boolean;
  canDownloadReport: boolean;
  isSaving: boolean;
  ratingsDirty: boolean;
  isLoading: boolean;
}

export interface RankingEvaluationWithTabsHandle {
  triggerSave: () => void;
  triggerDownloadManual: () => void;
  triggerDownloadReport: () => void;
  canSave: boolean;
  canDownloadManual: boolean;
  canDownloadReport: boolean;
  isSaving: boolean;
}

interface RankingEvaluationWithTabsProps {
  player: any;
  categoryId: string; // 'self-evaluation', 'coach-academy', 'practical-test'
  categoryLabel: string;
  coachId?: string;
  defaultSport?: string; // Optional: sport name to use as default
  subcategory?: string; // 'strokes-shots', 'drills', 'fitness'
  skillLevel?: string; // 'beginner', 'intermediate', 'advanced', 'pro'
  /** Hide header Save/Download when EvaluatorTabStrip provides them */
  hideHeaderSaveDownload?: boolean;
  /** Tab shell mode: hide title, score bar, and evaluated-by dropdown */
  embeddedInEvaluatorTabs?: boolean;
  onEmbeddedStateChange?: (state: RankingEvaluationEmbeddedState) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface SkillMediaItem {
  displayOrder?: number;
  sequence?: number;
  title?: string;
  url?: string;
  videoUrl?: string;
  name?: string;
  notes?: string;
  type?: 'video' | 'image';
  sponsorName?: string;
  sponsorLogoUrl?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ranking-tabpanel-${index}`}
      aria-labelledby={`ranking-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const RankingEvaluationWithTabs = forwardRef<RankingEvaluationWithTabsHandle, RankingEvaluationWithTabsProps>(function RankingEvaluationWithTabs({
  player,
  categoryId,
  categoryLabel,
  coachId,
  defaultSport: propDefaultSport,
  subcategory: propSubcategory = 'strokes-shots',
  skillLevel: propSkillLevel = 'beginner',
  hideHeaderSaveDownload = false,
  embeddedInEvaluatorTabs = false,
  onEmbeddedStateChange,
}, ref) {
  const { user } = useAuthStore();
  const location = useLocation();
  const isMobileOrTablet = useMediaQuery('(max-width:900px)');
  
  // Helper function to get auth token
  const getAuthToken = () => {
    return localStorage.getItem('accessToken') || localStorage.getItem('token');
  };
  
  // Helper function to get headers with auth token
  const getAuthHeaders = (additionalHeaders: Record<string, string> = {}) => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...additionalHeaders
    };
  };
  // Use subcategory prop to determine which content to show (no tabs, just show the selected subcategory)
  // Sync with URL params
  const urlParams = new URLSearchParams(location.search);
  const selectedSubcategory = urlParams.get('subcategory') || propSubcategory || 'strokes-shots';
  const isStrokeLikeSubcategory = selectedSubcategory === 'strokes-shots' || selectedSubcategory === 'shooting';
  const allowedSkillLevels = ['beginner', 'intermediate', 'advanced', 'pro'];
  const requestedLevel = String(urlParams.get('level') || propSkillLevel || '').toLowerCase();
  const playerLevel = String(player?.level || '').toLowerCase();
  const fallbackLevel = allowedSkillLevels.includes(playerLevel) ? playerLevel : 'beginner';
  const selectedSkillLevel = allowedSkillLevels.includes(requestedLevel) ? requestedLevel : fallbackLevel;
  const [strokeTypes, setStrokeTypes] = useState<any[]>([]);
  const [allStrokeTypes, setAllStrokeTypes] = useState<any[]>([]); // Store all strokes for filtering
  const [drills, setDrills] = useState<any[]>([]);
  const [fitness, setFitness] = useState<any[]>([]);
  const [ratings, setRatings] = useState<{ [code: string]: number }>({});
  // Period selection is now a stable period key (e.g. Q4-2025, Y-2025) – NOT a date string
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [periodType, setPeriodType] = useState<'quarterly' | 'half-yearly' | 'yearly'>('quarterly');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availablePeriods, setAvailablePeriods] = useState<Array<{value: string, label: string, isCompleted: boolean}>>([]);
  const [evaluationHistory, setEvaluationHistory] = useState<any[]>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingStrokes, setUploadingStrokes] = useState<Set<string>>(new Set()); // Track which strokes are uploading
  const [uploadedVideos, setUploadedVideos] = useState<Map<string, any>>(new Map()); // Track uploaded videos per stroke
  const [viewingVideo, setViewingVideo] = useState<{ url: string; filename: string } | null>(null); // Video viewer dialog
  const [compareVideos, setCompareVideos] = useState<{ referenceUrl: string; playerUrl: string; referenceLabel?: string; playerLabel?: string } | null>(null);
  const [viewingSkillDetails, setViewingSkillDetails] = useState<any | null>(null);
  const [viewingCoachFeedback, setViewingCoachFeedback] = useState<any | null>(null);
  const [bulkUploadRows, setBulkUploadRows] = useState<Array<{
    id: string;
    selectedCodes: string[];
    strokes: any[];
    file: File | null;
    uploading: boolean;
  }>>([]);
  
  // Dialog state for stroke code selection
  const [strokeSelectDialog, setStrokeSelectDialog] = useState<{
    open: boolean;
    rowId: string | null;
    searchTerm: string;
  }>({
    open: false,
    rowId: null,
    searchTerm: ''
  });
  
  // Bulk rating state
  const [selectedStrokes, setSelectedStrokes] = useState<Set<string>>(new Set());
  const [bulkRating, setBulkRating] = useState<number>(0);
  
  // Filter states
  const [filterCourtPosition, setFilterCourtPosition] = useState<string>('');
  const [filterHalfSide, setFilterHalfSide] = useState<string>('');
  const [filterStrokeType, setFilterStrokeType] = useState<string>('');
  const [filterPlayType, setFilterPlayType] = useState<string>('');
  const [filterShotType, setFilterShotType] = useState<string>('');
  const [filterDifficulty, setFilterDifficulty] = useState<string>('');
  const [filterRatedStatus, setFilterRatedStatus] = useState<string>('all'); // 'all', 'rated', 'not-rated', '1', '2', '3', '4', '5'
  
  // Score calculations
  const [totalScore, setTotalScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0); // Accumulated max (all 241 skills * 5)
  const [ratedScore, setRatedScore] = useState(0); // Score for rated items only
  const [ratedMaxScore, setRatedMaxScore] = useState(0); // Max score for rated items only (rated strokes * 5)
  const [backcourtScore, setBackcourtScore] = useState(0);
  const [backcourtMax, setBackcourtMax] = useState(0);
  const [midcourtScore, setMidcourtScore] = useState(0);
  const [midcourtMax, setMidcourtMax] = useState(0);
  const [frontcourtScore, setFrontcourtScore] = useState(0);
  const [frontcourtMax, setFrontcourtMax] = useState(0);

  const isPracticalTest = categoryId === 'practical-test';
  const isCoachAcademy = categoryId === 'coach-academy';
  const isSelfEvaluation = categoryId === 'self-evaluation';
  const isPlayer = user?.role === 'player';
  const isBusinessTeam = user?.role === 'admin' || (user?.role as string) === 'business'; // Business team can evaluate
  const isCoach = user?.role === 'coach';
  
  // Rating permissions:
  // - Players: can only rate self-evaluation
  // - Practical test: only business/admin can rate
  // - Coach/Academy: coaches can rate ONLY when acting as an evaluator (i.e. coachId is provided by coach pages)
  //   This prevents "My Rankings -> Coach/Academy" from accidentally allowing a coach to rate their own coach-academy eval.
  const canRate = isPlayer
    ? isSelfEvaluation
    : (
        isPracticalTest
          ? isBusinessTeam
          : (isCoachAcademy && isCoach && !coachId ? false : true)
      );

  const shouldShowEvaluatedByDropdown = isCoachAcademy && !canRate;
  
  // Practical test video upload is a player action.
  // Coaches can also act as players in "My Rankings" (coach-as-player) where coachId prop is NOT provided.
  const canUploadVideo = isPracticalTest && (isPlayer || (isCoach && !coachId));
  
  // Selected sport:
  // - default to player's primary sport
  // - but allow overriding via URL query (?sport=Badminton) when navigating from dashboard reports
  const defaultSport = propDefaultSport || player?.primarySport || player?.user?.primarySport || 'Badminton';
  const [selectedSport, setSelectedSport] = useState<string>(defaultSport);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(true);
  const isFootballSport = String(selectedSport).toLowerCase() === 'football' || selectedSubcategory === 'shooting';
  const strokeLabels = {
    courtPosition: isFootballSport ? 'Goal Target Zone' : 'Court Position',
    halfSide: isFootballSport ? 'Shooting Position' : 'Half Court',
    strokeType: isFootballSport ? 'Player Position' : 'Stroke Type',
    strokeAction: isFootballSport ? 'Distance (yards)' : 'Stroke Action',
    playType: isFootballSport ? 'Shooting Method' : 'Play Type',
    shotType: isFootballSport ? 'Area of Improvement' : 'Shot Type',
    placement: isFootballSport ? 'Possible Pitfalls' : 'Placement',
    difficulty: isFootballSport ? 'Shooting Rating' : 'Difficulty',
  };
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [coachName, setCoachName] = useState<string>('N/A');
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);
  const [linkedCoachOptions, setLinkedCoachOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedLinkedCoachId, setSelectedLinkedCoachId] = useState<string>('');
  const [rankingViewMode, setRankingViewMode] = useState<'table' | 'accordion'>('accordion');
  const [ratingsDirty, setRatingsDirty] = useState(false);
  const [savedRatings, setSavedRatings] = useState<{ [code: string]: number }>({});
  const [lastSavedRankingFeedback, setLastSavedRankingFeedback] = useState<any | null>(null);
  const [skillTrendByCode, setSkillTrendByCode] = useState<Record<string, Array<{ rating: number; label: string; at: string }>>>({});
  const [loadingTrendCodes, setLoadingTrendCodes] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(embeddedInEvaluatorTabs);
  const [expandedAccordionCode, setExpandedAccordionCode] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<number | null>(null);
  const downgradedCodes = useMemo(() => {
    const codes = new Set<string>();
    Object.keys(ratings || {}).forEach((code) => {
      const previous = Number(savedRatings?.[code] || 0);
      const current = Number(ratings?.[code] || 0);
      if (previous > 0 && current > 0 && current < previous) {
        codes.add(code);
      }
    });
    return codes;
  }, [ratings, savedRatings]);

  useEffect(() => {
    if (embeddedInEvaluatorTabs || isMobileOrTablet) {
      setRankingViewMode('accordion');
    }
  }, [embeddedInEvaluatorTabs, isMobileOrTablet]);

  // Keep selected sport in sync with URL/query + player changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sportParam = urlParams.get('sport');
    if (sportParam && sportParam.trim()) {
      setSelectedSport(sportParam.trim());
      return;
    }
    setSelectedSport(defaultSport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, defaultSport]);

  // For players viewing Coach/Academy evaluation, load linked coaches for "Evaluated By" dropdown.
  useEffect(() => {
    const loadLinkedCoaches = async () => {
      if (!player?.id || !shouldShowEvaluatedByDropdown) return;
      try {
        const resp = await fetch(`/api/players/${player.id}/coaches`, {
          headers: getAuthHeaders()
        });
        const data = await resp.json();
        const options: Array<{ id: string; name: string }> = Array.isArray(data?.data)
          ? data.data
              .filter((c: any) => c?.id)
              .map((c: any) => ({
                id: String(c.id),
                name:
                  (c.name && String(c.name).trim()) ||
                  `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
                  c.coachIdCode ||
                  `Coach ${String(c.id).substring(0, 8)}`,
              }))
          : [];

        setLinkedCoachOptions(options);
        if (!selectedLinkedCoachId && options.length > 0) {
          setSelectedLinkedCoachId(options[0].id);
        }
      } catch (e) {
        console.error('Error loading linked coaches for player:', e);
        setLinkedCoachOptions([]);
      }
    };

    loadLinkedCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, shouldShowEvaluatedByDropdown]);

  // Keep coachName in sync with selected linked coach (player view of coach-academy)
  useEffect(() => {
    if (!shouldShowEvaluatedByDropdown) return;
    if (!selectedLinkedCoachId) return;
    const match = linkedCoachOptions.find((c) => c.id === selectedLinkedCoachId);
    if (match?.name) setCoachName(match.name);
  }, [shouldShowEvaluatedByDropdown, selectedLinkedCoachId, linkedCoachOptions]);

  // Helper function to convert difficulty to number (1-5) for star rating
  const normalizeDifficultyValue = (value: string | number | undefined | null): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string' && value.trim() === '') return 0;
    
    if (typeof value === 'number') {
      return Math.max(0, Math.min(5, Math.round(value)));
    }
    
    const str = String(value).trim();
    const num = parseInt(str, 10);
    if (!isNaN(num) && num >= 0 && num <= 5) {
      return num;
    }
    
    const lowerStr = str.toLowerCase();
    if (lowerStr === 'low' || lowerStr === '1') return 1;
    if (lowerStr === 'medium' || lowerStr === '2' || lowerStr === '3') return 3;
    if (lowerStr === 'high' || lowerStr === '4' || lowerStr === '5') return 5;
    
    return 0;
  };

  // Helper functions for styling (reused from SportPage)
  const getCourtPositionStyle = (position: string) => {
    if (!position) return { style: {}, colorBox: null };
    const pos = position.toLowerCase().trim();
    if (pos.includes('backcourt') || pos.includes('back court')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          bottom: '4px',
          left: '4px',
          right: '4px',
          height: '25%',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    if (pos.includes('midcourt') || pos.includes('mid court')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          top: '50%',
          left: '4px',
          right: '4px',
          height: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    if (pos.includes('frontcourt') || pos.includes('front court')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          top: '4px',
          left: '4px',
          right: '4px',
          height: '25%',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    return { style: {}, colorBox: null };
  };

  const getHalfSideStyle = (side: string) => {
    if (!side) return { style: {}, colorBox: null };
    const s = side.toLowerCase().trim();
    if (s.includes('right')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          right: '4px',
          top: '4px',
          bottom: '4px',
          width: 'calc(50% - 4px)',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    if (s.includes('left')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          left: '4px',
          top: '4px',
          bottom: '4px',
          width: 'calc(50% - 4px)',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    return { style: {}, colorBox: null };
  };

  const getStrokeTypeColor = (type: string) => {
    if (!type) return 'transparent';
    const t = type.toLowerCase();
    const colors: { [key: string]: string } = {
      'forehand': '#e3f2fd',
      'backhand': '#fff3e0',
      'overhead': '#f3e5f5',
      'underarm': '#e8f5e9',
    };
    return colors[t] || 'transparent';
  };

  const getStrokeActionColor = (action: string) => {
    if (!action) return 'transparent';
    const a = action.toLowerCase();
    const colors: { [key: string]: string } = {
      'overhead': '#e1f5fe',
      'underarm': '#fce4ec',
    };
    return colors[a] || 'transparent';
  };

  const getShotTypeColor = (shot: string) => {
    if (!shot) return 'transparent';
    const s = shot.toLowerCase();
    const colors: { [key: string]: string } = {
      'smash': '#ffebee',
      'drop': '#e8f5e9',
      'clear': '#e3f2fd',
      'net': '#fff9c4',
    };
    return colors[s] || 'transparent';
  };

  const getDisplayRatingForStroke = (strokeCode: string): number => {
    let displayRating = ratings[strokeCode] || 0;
    if (isPracticalTest && uploadedVideos.has(strokeCode)) {
      const videoAttempts = uploadedVideos.get(strokeCode);
      if (Array.isArray(videoAttempts) && videoAttempts.length > 0) {
        const verifiedAttempts = videoAttempts.filter((v: any) =>
          v.status === 'verified' || v.status === 'approved' || v.status === 'validated'
        );
        if (verifiedAttempts.length > 0) {
          const bestAttempt = verifiedAttempts.reduce((best: any, current: any) =>
            (current.rating || 0) > (best.rating || 0) ? current : best
          );
          displayRating = bestAttempt.rating ? Number(bestAttempt.rating) : 0;
        }
      } else if (videoAttempts && !Array.isArray(videoAttempts)) {
        if (
          (videoAttempts.status === 'verified' ||
            videoAttempts.status === 'approved' ||
            videoAttempts.status === 'validated') &&
          videoAttempts.rating
        ) {
          displayRating = Number(videoAttempts.rating);
        }
      }
    }
    return displayRating;
  };

  const getHalfCourtLabel = (halfSide: string): string => {
    if (!halfSide) return '-';
    if (isFootballSport) return halfSide;
    const value = halfSide.toLowerCase();
    if (value.includes('right service court') || value.includes('right side') || value.includes('right')) {
      return 'Right Side';
    }
    if (value.includes('left service court') || value.includes('left side') || value.includes('left')) {
      return 'Left Side';
    }
    return halfSide;
  };

  const handleAccordionChange = (event: React.SyntheticEvent, code: string, expanded: boolean) => {
    const target = event.target as HTMLElement | null;
    const clickedExpandIcon = !!target?.closest('.MuiAccordionSummary-expandIconWrapper');
    if (!clickedExpandIcon) {
      return;
    }
    setExpandedAccordionCode(expanded ? code : null);
    if (expanded) {
      void loadSkillTrend(code);
    }
  };

  const getActiveSkillMediaItems = (items?: SkillMediaItem[]) =>
    (items || [])
      .map((item, index) => ({
        ...item,
        url: item?.url || item?.videoUrl || '',
        displayOrder: item?.displayOrder ?? item?.sequence ?? index + 1,
      }))
      .filter((item) => item?.url)
      .sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));

  const getSkillMediaCounts = (strokeType: any) => {
    const structuredCount = normalizeStrokeVideoSections(strokeType?.strokeVideoSections, strokeType?.referenceVideos)
      .reduce((count, section) => count + (section.videos || []).filter((video) => video.videoUrl).length, 0);
    const regularCount = getActiveSkillMediaItems(strokeType?.regularTrainingVideos).length;
    const creatorCount = getActiveSkillMediaItems(strokeType?.creatorReferenceVideos).length;
    const amateurCount = getActiveSkillMediaItems(strokeType?.amateurSampleVideos).length;
    const contestCount = getActiveSkillMediaItems(strokeType?.contestVideos).length;
    return {
      structuredCount,
      regularCount,
      creatorCount,
      amateurCount,
      contestCount,
      total: structuredCount + regularCount + creatorCount + amateurCount + contestCount,
    };
  };

  const normalizeSkillOptionList = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      const cleaned = value.map((item) => String(item || '').trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned : ['Consult the coaches'];
    }
    if (typeof value === 'string') {
      const cleaned = value.split(',').map((item) => item.trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned : ['Consult the coaches'];
    }
    return ['Consult the coaches'];
  };

  const renderOptionList = (title: string, value: unknown) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 800 }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {normalizeSkillOptionList(value).map((option) => (
          <Typography component="li" variant="body2" key={`${title}-${option}`} sx={{ mb: 0.25 }}>
            {option}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  const getSkillIdentityLine = (strokeType: any) =>
    [
      strokeType?.courtPosition,
      strokeType?.halfSide,
      strokeType?.strokeType,
      strokeType?.strokeAction,
      strokeType?.shotType,
      strokeType?.placement,
    ].filter(Boolean).join(' | ') || strokeType?.code || 'Skill';

  const getShotTypeSx = (shotType?: string) => {
    const normalized = String(shotType || '').toLowerCase();
    if (normalized.includes('smash') || normalized.includes('kill')) return { bgcolor: '#ffebee', color: '#b71c1c', borderColor: '#ffcdd2' };
    if (normalized.includes('drop') || normalized.includes('net')) return { bgcolor: '#e8f5e9', color: '#1b5e20', borderColor: '#c8e6c9' };
    if (normalized.includes('clear') || normalized.includes('lift')) return { bgcolor: '#e3f2fd', color: '#0d47a1', borderColor: '#bbdefb' };
    if (normalized.includes('drive') || normalized.includes('push')) return { bgcolor: '#fff3e0', color: '#e65100', borderColor: '#ffe0b2' };
    return { bgcolor: '#f3e5f5', color: '#4a148c', borderColor: '#e1bee7' };
  };

  const renderSkillIdentity = (strokeType: any, compact = false) => {
    const segments = [
      { label: strokeLabels.courtPosition, value: strokeType?.courtPosition, sx: { color: '#0d47a1', fontWeight: 700 } },
      { label: strokeLabels.halfSide, value: strokeType?.halfSide, sx: { color: '#1565c0', fontWeight: 700 } },
      { label: strokeLabels.strokeType, value: strokeType?.strokeType, sx: { color: '#263238', fontWeight: 700 } },
      { label: strokeLabels.strokeAction, value: strokeType?.strokeAction, sx: { color: '#374151', fontWeight: 700 } },
      { label: strokeLabels.shotType, value: strokeType?.shotType, sx: { ...getShotTypeSx(strokeType?.shotType), px: 0.45, py: 0.1, borderRadius: 0.75, fontWeight: 900 } },
      { label: strokeLabels.placement, value: strokeType?.placement, sx: { color: '#4a148c', fontWeight: 700 } },
    ].filter((segment) => Boolean(segment.value));

    if (segments.length === 0) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 900, color: '#102a43' }}>
          {strokeType?.code || 'Skill'}
        </Typography>
      );
    }

    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          component="div"
          sx={{
            fontSize: compact ? '0.78rem' : '0.86rem',
            lineHeight: 1.35,
            color: '#102a43',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {segments.map((segment, index) => (
            <React.Fragment key={`${segment.label}-${segment.value}`}>
              {index > 0 && (
                <Typography component="span" variant="inherit" sx={{ color: 'text.secondary', mx: 0.45 }}>
                  |
                </Typography>
              )}
              <Typography component="span" variant="inherit" sx={segment.sx}>
                {segment.label === strokeLabels.shotType ? String(segment.value) : (compact ? String(segment.value) : `${segment.label}: ${segment.value}`)}
              </Typography>
            </React.Fragment>
          ))}
        </Typography>
      </Box>
    );
  };

  const renderSkillActionButtons = (strokeType: any) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
      <Button
        size="small"
        variant="contained"
        startIcon={<SmartDisplayIcon />}
        onClick={(event) => {
          event.stopPropagation();
          setViewingSkillDetails(strokeType);
        }}
        sx={{
          textTransform: 'none',
          minHeight: 24,
          px: 0.85,
          py: 0.15,
          fontSize: '0.72rem',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          bgcolor: '#1565c0',
          boxShadow: 'none',
          '& .MuiButton-startIcon': { mr: 0.35 },
          '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' },
        }}
      >
        Training Videos
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<VisibilityIcon />}
        onClick={(event) => {
          event.stopPropagation();
          setViewingCoachFeedback(strokeType);
        }}
        sx={{
          textTransform: 'none',
          minHeight: 24,
          px: 0.85,
          py: 0.15,
          fontSize: '0.72rem',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          bgcolor: '#6d28d9',
          borderColor: '#6d28d9',
          borderWidth: 1.5,
          color: '#fff',
          '& .MuiButton-startIcon': { mr: 0.35 },
          '&:hover': { bgcolor: '#5b21b6', borderColor: '#5b21b6', borderWidth: 1.5 },
        }}
      >
        Coach Feedback
      </Button>
    </Stack>
  );

  const renderImageActionIcons = (title: string, imageUrls?: string[]) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
      {(imageUrls || []).slice(0, 3).map((imageUrl, imageIndex) => (
        <Tooltip title={`View image ${imageIndex + 1}`} key={`${title}-image-${imageIndex}`}>
          <IconButton
            size="small"
            component={Link}
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#7b1fa2' }}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ))}
      {(imageUrls || []).length > 3 ? (
        <Chip size="small" label={`+${(imageUrls || []).length - 3}`} sx={{ height: 20 }} />
      ) : null}
    </Box>
  );

  const renderVideoActionIcon = (videoUrl: string, title: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {videoUrl ? (
        <Tooltip title="View video">
          <IconButton
            size="small"
            color="primary"
            onClick={() => setViewingVideo({ url: videoUrl, filename: title })}
          >
            <SmartDisplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );

  const getMediaSponsorName = (item: any): string =>
    String(item?.sponsorName || item?.sponsor_name || item?.brandName || item?.sponsor?.name || item?.sponsor || '').trim();

  const getMediaSponsorLogoUrl = (item: any): string =>
    String(item?.sponsorLogoUrl || item?.sponsorLogo || item?.sponsor_logo_url || item?.logoUrl || item?.sponsor?.logoUrl || '').trim().replace(/\\/g, '/');

  const renderSponsorCell = (sponsorName?: string, sponsorLogoUrl?: string) => {
    const name = String(sponsorName || '').trim();
    const logoUrl = String(sponsorLogoUrl || '').trim();
    if (!name && !logoUrl) return <Typography variant="caption" color="text.secondary">-</Typography>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={name || 'Sponsor'}
            sx={{ width: 44, height: 34, objectFit: 'contain', borderRadius: 1, bgcolor: '#fff', border: '1px solid #e0e7ef', p: 0.25, flexShrink: 0 }}
          />
        ) : null}
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#5d4037', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {name || 'Sponsor'}
        </Typography>
      </Box>
    );
  };

  const renderCompactMediaTable = (
    title: string,
    rows: Array<{
      key: string;
      sequence?: number;
      sequenceLabel?: string;
      sortSequence?: number;
      title: string;
      description?: string;
      videoUrl?: string;
      imageUrls?: string[];
      sponsorName?: string;
      sponsorLogoUrl?: string;
    }>,
    gradient: string,
    entryLabel = 'Skill'
  ) => (
    <Box sx={{ border: '1px solid #dbe8f6', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ px: 1.25, py: 0.85, background: gradient, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#102a43' }}>
          {title} ({rows.length})
        </Typography>
        {getDistinctSponsors(rows).slice(0, 3).map((sponsor, sponsorIndex) => (
          <Box key={`${title}-sponsor-${sponsor.sponsorName}-${sponsorIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 0.5 }}>
            {sponsor.sponsorLogoUrl ? (
              <Box
                component="img"
                src={sponsor.sponsorLogoUrl}
                alt={sponsor.sponsorName || 'Sponsor'}
                sx={{ width: 38, height: 30, objectFit: 'contain', borderRadius: 1, bgcolor: '#fff', border: '1px solid #e0e7ef', p: 0.25 }}
              />
            ) : null}
            <Typography variant="caption" sx={{ fontWeight: 900, color: '#5d4037' }}>
              {sponsor.sponsorName || 'Sponsor'}
            </Typography>
          </Box>
        ))}
      </Box>
      {rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.25, fontStyle: 'italic' }}>
          No videos added.
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 320, overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 82, py: 0.65 }}>Sequence</TableCell>
                <TableCell sx={{ fontWeight: 800, py: 0.65 }}>{entryLabel}</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 80, textAlign: 'center', py: 0.65, whiteSpace: 'nowrap' }}>Video</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 150, py: 0.65, whiteSpace: 'nowrap' }}>Sponsor</TableCell>
                <TableCell sx={{ fontWeight: 800, py: 0.65 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 90, textAlign: 'center', py: 0.65, whiteSpace: 'nowrap' }}>Images</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.key} hover>
                  <TableCell sx={{ py: 0.55 }}>
                    <Chip size="small" label={row.sequenceLabel || row.sequence || index + 1} sx={{ height: 22, fontWeight: 900 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 0.55 }}>
                    {row.title || `${entryLabel} ${index + 1}`}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.35 }}>
                    {renderVideoActionIcon(row.videoUrl || '', row.title || `${entryLabel} ${index + 1}`)}
                  </TableCell>
                  <TableCell sx={{ py: 0.45 }}>
                    {renderSponsorCell(row.sponsorName, row.sponsorLogoUrl)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', py: 0.55 }}>
                    {row.description || '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.35 }}>
                    {renderImageActionIcons(row.title || `${entryLabel} ${index + 1}`, row.imageUrls)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const getStructuredLearningGroups = (strokeType: any) =>
    normalizeStrokeVideoSections(strokeType?.strokeVideoSections, strokeType?.referenceVideos)
      .map((section, sectionIndex) => {
        const sectionSequence = Number(section.displayOrder || sectionIndex + 1);
        const activeVideos = (section.videos || [])
          .filter((video) => video.videoUrl || video.title || video.text || (video.imageUrls || []).length > 0)
          .sort((a, b) => Number(a.displayOrder ?? a.sequence ?? 9999) - Number(b.displayOrder ?? b.sequence ?? 9999));
        const rows = activeVideos
          .map((video, videoIndex) => {
            const rawTitle = String(video.title || '').trim();
            const videoSequence = Number(video.displayOrder ?? video.sequence ?? videoIndex + 1);
            return {
              key: `${section.key}-${videoIndex}-${video.videoUrl || rawTitle}`,
              sequence: sectionSequence,
              sequenceLabel: `${sectionSequence}.${videoSequence}`,
              sortSequence: (sectionSequence * 1000) + videoSequence,
              title: rawTitle || `Step ${videoIndex + 1}`,
              description: video.text || '',
              videoUrl: video.videoUrl || '',
              imageUrls: video.imageUrls || [],
              sponsorName: getMediaSponsorName(video),
              sponsorLogoUrl: getMediaSponsorLogoUrl(video),
            };
          })
          .sort((a, b) => (a.sortSequence || 999999) - (b.sortSequence || 999999));
        return {
          key: section.key,
          title: section.label,
          sequence: sectionSequence,
          rows,
        };
      })
      .filter((group) => group.rows.length > 0)
      .sort((a, b) => a.sequence - b.sequence);

  const buildAdditionalVideoRows = (items: SkillMediaItem[] | undefined, categoryLabel: string) =>
    getActiveSkillMediaItems(items).map((item, index) => ({
      key: `${categoryLabel}-${index}-${item.url}`,
      sequence: Number(item.displayOrder || index + 1),
      title: item.title || `${categoryLabel} Video ${index + 1}`,
      description: [item.name, item.notes].filter(Boolean).join(' | '),
      videoUrl: item.type === 'image' ? '' : item.url || '',
      imageUrls: item.type === 'image' && item.url ? [item.url] : [],
      sponsorName: getMediaSponsorName(item),
      sponsorLogoUrl: getMediaSponsorLogoUrl(item),
    }));

  const getAdditionalVideoGroups = (strokeType: any) => [
    {
      key: 'regular',
      title: 'Regular Training Videos',
      rows: buildAdditionalVideoRows(strokeType?.regularTrainingVideos, 'Regular'),
      gradient: 'linear-gradient(90deg, #e8f5e9 0%, #f1f8e9 100%)',
    },
    {
      key: 'creator',
      title: 'Creator / Reference Videos',
      rows: buildAdditionalVideoRows(strokeType?.creatorReferenceVideos, 'Creator'),
      gradient: 'linear-gradient(90deg, #e3f2fd 0%, #ede7f6 100%)',
    },
    {
      key: 'amateur',
      title: 'Amateur Videos',
      rows: buildAdditionalVideoRows(strokeType?.amateurSampleVideos, 'Amateur'),
      gradient: 'linear-gradient(90deg, #fff3e0 0%, #fce4ec 100%)',
    },
    {
      key: 'contest',
      title: 'Contest Videos',
      rows: buildAdditionalVideoRows(strokeType?.contestVideos, 'Contest'),
      gradient: 'linear-gradient(90deg, #ede7f6 0%, #e0f7fa 100%)',
    },
  ];

  const getDistinctSponsors = (rows: Array<{ sponsorName?: string; sponsorLogoUrl?: string }>) => {
    const sponsors = new Map<string, { sponsorName: string; sponsorLogoUrl: string }>();
    rows.forEach((row) => {
      const sponsorName = String(row.sponsorName || '').trim();
      const sponsorLogoUrl = String(row.sponsorLogoUrl || '').trim();
      if (!sponsorName && !sponsorLogoUrl) return;
      const key = `${sponsorName.toLowerCase()}|${sponsorLogoUrl.toLowerCase()}`;
      sponsors.set(key, { sponsorName, sponsorLogoUrl });
    });
    return Array.from(sponsors.values());
  };

  const renderMediaCategoryAccordion = (
    title: string,
    count: number,
    gradient: string,
    children: React.ReactNode
  ) => (
    <Accordion disableGutters sx={{ border: '1px solid #dbe8f6', borderRadius: '12px !important', overflow: 'hidden', boxShadow: 'none', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: gradient, minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#102a43' }}>
          {title} ({count})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, bgcolor: '#fff' }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );

  const renderSkillDetailsDialogContent = (strokeType: any) => {
    if (!strokeType) return null;
    const counts = getSkillMediaCounts(strokeType);
    const structuredGroups = getStructuredLearningGroups(strokeType);
    const additionalGroups = getAdditionalVideoGroups(strokeType);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip size="small" label={`Total Videos (${counts.total})`} variant="outlined" />
          <Chip size="small" label={`Learning (${counts.structuredCount})`} variant="outlined" />
          <Chip size="small" label={`Regular (${counts.regularCount})`} variant="outlined" />
          <Chip size="small" label={`Creator (${counts.creatorCount})`} variant="outlined" />
          <Chip size="small" label={`Amateur (${counts.amateurCount})`} variant="outlined" />
          <Chip size="small" label={`Contest (${counts.contestCount})`} variant="outlined" />
        </Box>

        {renderMediaCategoryAccordion(
          'Structured Learning Videos',
          counts.structuredCount,
          'linear-gradient(90deg, #e3f2fd 0%, #e8f5e9 100%)',
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {structuredGroups.length > 0 ? (
            structuredGroups.map((group) =>
              renderCompactMediaTable(
                `${group.sequence}. ${group.title}`,
                group.rows,
                'linear-gradient(90deg, #e3f2fd 0%, #e8f5e9 100%)',
                'Step'
              )
            )
          ) : (
            renderCompactMediaTable(
              'Structured Learning Steps and Videos',
              [],
              'linear-gradient(90deg, #e3f2fd 0%, #e8f5e9 100%)',
              'Step'
            )
          )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {additionalGroups.map((group) =>
            renderMediaCategoryAccordion(
              group.title,
              group.rows.length,
              group.gradient,
              renderCompactMediaTable(
                group.title,
                group.rows,
                group.gradient,
                'Video'
              )
            )
          )}
        </Box>
      </Box>
    );
  };

  const renderCoachFeedbackDialogContent = (strokeType: any) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ border: '1px solid #dbe8f6', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ px: 1.25, py: 0.85, background: 'linear-gradient(90deg, #fff8e1 0%, #fce4ec 100%)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#5d4037' }}>
            Coach Feedback
          </Typography>
        </Box>
        <Grid container spacing={1.5} sx={{ p: 1.25 }}>
          <Grid item xs={12} md={6}>
            {renderOptionList('Area of Improvement', strokeType?.areaOfImprovementOptions)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderOptionList('Suggested Practices', strokeType?.suggestedPracticesOptions)}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // Period keys (disambiguates Q4-2025 vs Y-2025)
  const getPeriodEndDateFromKey = (periodKey: string): string => {
    const [prefix, yearStr] = String(periodKey).split('-');
    const year = Number(yearStr) || new Date().getFullYear();
    if (prefix === 'Q1') return `${year}-03-31`;
    if (prefix === 'Q2') return `${year}-06-30`;
    if (prefix === 'Q3') return `${year}-09-30`;
    if (prefix === 'Q4') return `${year}-12-31`;
    if (prefix === 'H1') return `${year}-06-30`;
    if (prefix === 'H2') return `${year}-12-31`;
    if (prefix === 'Y') return `${year}-12-31`;
    return `${year}-12-31`;
  };

  const inferPeriodTypeFromKey = (periodKey: string): 'quarterly' | 'half-yearly' | 'yearly' => {
    if (String(periodKey).startsWith('Q')) return 'quarterly';
    if (String(periodKey).startsWith('H')) return 'half-yearly';
    if (String(periodKey).startsWith('Y-')) return 'yearly';
    return 'quarterly';
  };

  /** Return period start and end dates (inclusive) for filtering videos by uploaded_at */
  const getPeriodStartEnd = (periodKey: string): { start: Date; end: Date } | null => {
    const [prefix, yearStr] = String(periodKey).split('-');
    const year = Number(yearStr);
    if (!Number.isFinite(year)) return null;
    let start: string, end: string;
    if (prefix === 'Q1') { start = `${year}-01-01`; end = `${year}-03-31`; }
    else if (prefix === 'Q2') { start = `${year}-04-01`; end = `${year}-06-30`; }
    else if (prefix === 'Q3') { start = `${year}-07-01`; end = `${year}-09-30`; }
    else if (prefix === 'Q4') { start = `${year}-10-01`; end = `${year}-12-31`; }
    else if (prefix === 'H1') { start = `${year}-01-01`; end = `${year}-06-30`; }
    else if (prefix === 'H2') { start = `${year}-07-01`; end = `${year}-12-31`; }
    else if (prefix === 'Y') { start = `${year}-01-01`; end = `${year}-12-31`; }
    else return null;
    return { start: new Date(start + 'T00:00:00'), end: new Date(end + 'T23:59:59.999') };
  };

  const getDefaultPeriodKey = (year: number, t: 'quarterly' | 'half-yearly' | 'yearly'): string => {
    if (t === 'yearly') return `Y-${year}`;
    if (t === 'half-yearly') return `H2-${year}`;
    return `Q4-${year}`;
  };

  // Helper function to get all available periods (based on periodType, for selected year)
  // periodTypeParam: optional parameter to override the state value (for immediate updates)
  const getAllAvailablePeriods = (year: number, periodTypeParam?: 'quarterly' | 'half-yearly' | 'yearly'): Array<{value: string, label: string, isCompleted: boolean}> => {
    const periods: Array<{value: string, label: string, isCompleted: boolean}> = [];
    // Use the parameter if provided, otherwise use state value
    const effectivePeriodType = periodTypeParam || periodType;
    
    // Only show periods for year 2025 and onwards (excluding 2024 and 2023)
    // Don't mark any periods as completed - all should be available
    if (year >= 2025) {
      if (effectivePeriodType === 'yearly') {
        periods.push({ value: `Y-${year}`, label: `Yearly ${year}`, isCompleted: false });
      } else if (effectivePeriodType === 'half-yearly') {
        periods.push({ value: `H1-${year}`, label: `H1 ${year}`, isCompleted: false });
        periods.push({ value: `H2-${year}`, label: `H2 ${year}`, isCompleted: false });
      } else {
        // Default to quarterly
        periods.push({ value: `Q1-${year}`, label: `Q1 ${year}`, isCompleted: false });
        periods.push({ value: `Q2-${year}`, label: `Q2 ${year}`, isCompleted: false });
        periods.push({ value: `Q3-${year}`, label: `Q3 ${year}`, isCompleted: false });
        periods.push({ value: `Q4-${year}`, label: `Q4 ${year}`, isCompleted: false });
      }
    }
    
    // Sort: most recent first
    return periods.sort((a, b) => b.value.localeCompare(a.value));
  };

  // Check URL for periodKey/date parameter on mount and when location changes
  // NOTE: This should NOT depend on player.id; otherwise the dropdown can end up with an out-of-range
  // selectedPeriodKey (e.g. Y-2025) while availablePeriods still reflect the default year (e.g. 2026).
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const periodKeyParam = urlParams.get('periodKey');
    const legacyDateParam = urlParams.get('date'); // legacy compatibility
    
    const effectiveKey = periodKeyParam || null;
    if (effectiveKey) {
      const year = Number(String(effectiveKey).split('-')[1]);
      if (Number.isFinite(year) && year >= 2025) {
        const inferredType = inferPeriodTypeFromKey(String(effectiveKey));
        setPeriodType(inferredType);
        setSelectedYear(year);
        setSelectedPeriodKey(String(effectiveKey));
      }
      return;
    }

    // Legacy: if a date was passed, map it to a yearly key for that year.
    if (legacyDateParam) {
      const dateStr = String(legacyDateParam).split('T')[0].split(' ')[0];
      const y = new Date(dateStr + 'T00:00:00').getFullYear();
      if (y >= 2025) {
        setPeriodType('yearly');
        setSelectedYear(y);
        setSelectedPeriodKey(`Y-${y}`);
      }
    }
  }, [location.search]);

  // Load data based on subcategory
  useEffect(() => {
    if (player?.id) {
      if (isStrokeLikeSubcategory) {
        loadStrokeTypes();
        loadEvaluationHistory();
      } else if (selectedSubcategory === 'drills') {
        loadDrills();
      } else if (selectedSubcategory === 'fitness') {
        loadFitness();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategory, categoryId, player?.id, selectedSkillLevel, selectedSport]);
  
  // Separate effect to update periods when year or periodType changes (without reloading data)
  useEffect(() => {
    if (isStrokeLikeSubcategory) {
      const periods = getAllAvailablePeriods(selectedYear, periodType);
      setAvailablePeriods(periods);
      // If current selectedPeriodKey is not in new periods, reset to first period
      if (selectedPeriodKey && !periods.find(p => p.value === selectedPeriodKey)) {
        setSelectedPeriodKey(periods[0]?.value || '');
      } else if (!selectedPeriodKey && periods.length > 0) {
        // Initialize selectedPeriodKey to first available period if it's empty
        setSelectedPeriodKey(periods[0]?.value || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, periodType, selectedSubcategory, isStrokeLikeSubcategory]);

  // Guard: if selectedPeriodKey is not present in availablePeriods (can happen during SPA navigation),
  // ensure availablePeriods matches the selectedPeriodKey year so MUI Select never becomes out-of-range.
  useEffect(() => {
    if (!selectedPeriodKey) return;
    if (availablePeriods.some(p => p.value === selectedPeriodKey)) return;
    const y = Number(String(selectedPeriodKey).split('-')[1]);
    if (Number.isFinite(y) && y >= 2025) {
      setSelectedYear(y);
      setAvailablePeriods(getAllAvailablePeriods(y, periodType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriodKey, availablePeriods]);

  // Removed periodic polling - page will only load when period is manually selected by user

  // Clear ratings when category changes to prevent showing wrong category's data
  // This is necessary to prevent showing self-evaluation ratings when switching to coach-academy, etc.
  useEffect(() => {
    // Always clear ratings when category changes (even if empty) to ensure clean state
    // But only log if there were actual ratings to clear
    const hadRatings = Object.keys(ratings).length > 0;
    if (hadRatings) {
      console.log(`[RankingEvaluation] Category changed to: ${categoryId}, clearing ${Object.keys(ratings).length} existing ratings to prevent showing wrong category data`);
    }
    setRatings({});
    setIsSubmitted(false);
  }, [categoryId, selectedSport]);

  // Load existing ratings ONLY when period is selected (not on every render)
  useEffect(() => {
    // Only load if we have all required data and a period is selected
    if (strokeTypes.length > 0 && selectedPeriodKey && player?.id) {
      console.log('[RankingEvaluation] Loading ratings for periodKey:', selectedPeriodKey, 'category:', categoryId, 'year:', selectedYear);
      loadExistingRatings();
    }
    // Depend on selectedPeriod AND categoryId to reload when category changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriodKey, categoryId, selectedSport, strokeTypes.length, player?.id]);

  // Load uploaded videos for Board Exam when period is selected
  useEffect(() => {
    if (canUploadVideo && selectedPeriodKey && player?.id && strokeTypes.length > 0) {
      console.log('[Practical Exam] useEffect triggered - loading videos. strokeTypes.length:', strokeTypes.length);
      loadUploadedVideos();
    } else {
      console.log('[Practical Exam] useEffect skipped - conditions not met:', {
        canUploadVideo,
        selectedPeriodKey,
        playerId: player?.id,
        strokeTypesLength: strokeTypes.length
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUploadVideo, selectedPeriodKey, player?.id, strokeTypes.length, selectedSport]);

  // Removed duplicate - score calculation is handled in the useEffect below

  const loadStrokeTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/ranking-stroke-types?sport=${encodeURIComponent(selectedSport)}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const activeStrokes = data.data
            .filter((st: any) => st.isActive !== false)
            .map((st: any) => ({
              ...st,
              difficulty: st.difficulty || '',
            }));
          activeStrokes.sort((a: any, b: any) => {
            if (isFootballSport) {
              return String(a.code || '').localeCompare(String(b.code || ''), undefined, { numeric: true, sensitivity: 'base' });
            }
            const orderDelta = (a.displayOrder || 0) - (b.displayOrder || 0);
            if (orderDelta !== 0) return orderDelta;
            return String(a.code || '').localeCompare(String(b.code || ''), undefined, { numeric: true, sensitivity: 'base' });
          });
          setAllStrokeTypes(activeStrokes); // Store all strokes for filtering
          setStrokeTypes(activeStrokes); // Initially show all
        }
      } else {
        setError('Failed to load stroke types');
      }
    } catch (err: any) {
      console.error('Error loading stroke types:', err);
      setError(err.message || 'Failed to load stroke types');
    } finally {
      setLoading(false);
    }
  };

  const loadDrills = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-drills?sport=${selectedSport}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // TODO: Filter by skill level based on skill_level_drill_mapping
          // For now, show all active drills
          const activeDrills = (data.data || [])
            .filter((d: any) => d.isActive !== false)
            .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
          setDrills(activeDrills);
        }
      } else {
        setError('Failed to load drills');
      }
    } catch (err: any) {
      console.error('Error loading drills:', err);
      setError(err.message || 'Failed to load drills');
    } finally {
      setLoading(false);
    }
  };

  const loadFitness = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-fitness?sport=${selectedSport}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // TODO: Filter by skill level based on skill_level_fitness_mapping
          // For now, show all active fitness
          const activeFitness = (data.data || [])
            .filter((f: any) => f.isActive !== false)
            .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
          setFitness(activeFitness);
        }
      } else {
        setError('Failed to load fitness');
      }
    } catch (err: any) {
      console.error('Error loading fitness:', err);
      setError(err.message || 'Failed to load fitness');
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (field: string) => {
    const values = allStrokeTypes
      .map((st: any) => st[field])
      .filter((val: any) => val && val.trim() !== '')
      .filter((val: any, index: number, self: any[]) => self.indexOf(val) === index)
      .sort();
    return values;
  };

  // Filter stroke types based on selected filters
  useEffect(() => {
    let filtered = [...allStrokeTypes];

    // Apply filters
    if (filterCourtPosition) {
      filtered = filtered.filter((st: any) => st.courtPosition === filterCourtPosition);
    }
    if (filterHalfSide) {
      filtered = filtered.filter((st: any) => st.halfSide === filterHalfSide);
    }
    if (filterStrokeType) {
      filtered = filtered.filter((st: any) => st.strokeType === filterStrokeType);
    }
    if (filterPlayType) {
      filtered = filtered.filter((st: any) => st.playType === filterPlayType);
    }
    if (filterDifficulty) {
      // Filter by difficulty - handle both numeric and legacy string values
      filtered = filtered.filter((st: any) => {
        const stDifficulty = normalizeDifficultyValue(st.difficulty);
        const filterDifficultyNum = normalizeDifficultyValue(filterDifficulty);
        return stDifficulty === filterDifficultyNum;
      });
    }
    if (filterShotType) {
      filtered = filtered.filter((st: any) => st.shotType === filterShotType);
    }
    
    // Apply rated/not-rated filter
    if (filterRatedStatus === 'rated') {
      filtered = filtered.filter((st: any) => ratings[st.code] && ratings[st.code] > 0);
    } else if (filterRatedStatus === 'not-rated') {
      filtered = filtered.filter((st: any) => !ratings[st.code] || ratings[st.code] === 0);
    } else if (['1', '2', '3', '4', '5'].includes(filterRatedStatus)) {
      // Filter by specific rating value
      const ratingValue = parseInt(filterRatedStatus);
      filtered = filtered.filter((st: any) => ratings[st.code] === ratingValue);
    }
    // 'all' shows everything, no additional filtering needed

    setStrokeTypes(filtered);
  }, [filterCourtPosition, filterHalfSide, filterStrokeType, filterPlayType, 
      filterShotType, filterDifficulty, filterRatedStatus, allStrokeTypes, ratings]);

  const loadExistingRatings = async () => {
    if (!player?.id || !selectedPeriodKey) {
      // Clear ratings if no period selected
      setRatings({});
      setSavedRatings({});
      setLastSavedRankingFeedback(null);
      setIsSubmitted(false);
      setCurrentReportId(null);
      return;
    }
    setLoadingRatings(true);
    try {
      // v2: lookup report by keys, then fetch by reportId
      const qs = new URLSearchParams({
        playerId: String(player.id),
        categoryId: String(categoryId),
        sport: String(selectedSport),
        periodKey: String(selectedPeriodKey),
      });
      const lookupRes = await fetch(`/api/ranking-reports/lookup?${qs.toString()}`, {
        headers: getAuthHeaders()
      });
      const lookupData = await lookupRes.json();
      if (!lookupRes.ok || !lookupData.success) {
        throw new Error(lookupData.message || 'Failed to lookup report');
      }

      if (!lookupData.data?.exists || !lookupData.data?.reportId) {
        setRatings({});
        setSavedRatings({});
        setLastSavedRankingFeedback(null);
        setIsSubmitted(false);
        setCurrentReportId(null);
        return;
      }

      const reportId = String(lookupData.data.reportId);
      setCurrentReportId(reportId);

      const reportRes = await fetch(`/api/ranking-reports/${encodeURIComponent(reportId)}`, {
        headers: getAuthHeaders()
      });
      const reportData = await reportRes.json();
      if (!reportRes.ok || !reportData.success) {
        throw new Error(reportData.message || 'Failed to fetch report');
      }

      const strokes = reportData.data?.strokes || [];
      const ratingsMap: { [code: string]: number } = {};
      strokes.forEach((s: any) => {
        if (s?.code) ratingsMap[s.code] = Number(s.rating) || 0;
      });
      setRatings(ratingsMap);
      setSavedRatings(ratingsMap);
      setLastSavedRankingFeedback(null);
      setIsSubmitted(reportData.data?.status === 'submitted');
    } catch (err: any) {
      console.error(`[RankingEvaluation] Error loading ratings for category ${categoryId}:`, err);
      setError(null);
      setRatings({});
      setSavedRatings({});
      setLastSavedRankingFeedback(null);
      setIsSubmitted(false);
      setCurrentReportId(null);
    } finally {
      setLoadingRatings(false);
    }
  };

  // Load uploaded videos for Board Exam
  const loadUploadedVideos = async () => {
    console.log('[Practical Exam] loadUploadedVideos called with:', {
      canUploadVideo,
      playerId: player?.id,
      selectedPeriodKey,
      strokeTypesLength: strokeTypes.length,
      categoryId,
      isPracticalTest
    });
    
    if (!canUploadVideo || !player?.id || !selectedPeriodKey) {
      console.log('[Practical Exam] ⚠️ Early return - conditions not met:', {
        canUploadVideo,
        hasPlayerId: !!player?.id,
        hasPeriodKey: !!selectedPeriodKey
      });
      setUploadedVideos(new Map());
      return;
    }

    try {
      const sport = selectedSport || 'Badminton';
      const examType = 'stroke-shot';
      
      console.log('[Practical Exam] Loading uploaded videos for player:', player.id, 'sport:', sport);
      
      // Fetch ALL videos for this player (not just latest) to show all attempts
      const response = await fetch(
        `/api/practical-exam/videos?playerId=${player.id}&sport=${sport}&examType=${examType}&limit=1000`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();
      
      console.log('[Practical Exam] API response:', {
        success: data.success,
        videoCount: data.data?.videos?.length || 0,
        hasData: !!data.data,
        responseKeys: Object.keys(data)
      });
      
      if (data.success && data.data?.videos) {
        // Filter by selected period: only show videos uploaded within this period's date range
        const periodRange = getPeriodStartEnd(selectedPeriodKey);
        const videosInPeriod = periodRange
          ? data.data.videos.filter((v: any) => {
              const uploadedAt = v.uploaded_at ? new Date(v.uploaded_at).getTime() : 0;
              return uploadedAt >= periodRange.start.getTime() && uploadedAt <= periodRange.end.getTime();
            })
          : data.data.videos;
        console.log('[Practical Exam] Found', data.data.videos.length, 'uploaded videos;', videosInPeriod.length, 'in period', selectedPeriodKey);
        const strokeCodesFromAPI = videosInPeriod.map((v: any) => v.stroke_code).filter(Boolean);
        console.log('[Practical Exam] Video stroke codes from API:', strokeCodesFromAPI.length > 0 ? strokeCodesFromAPI.join(', ') : 'NONE');
        
        const videosMap = new Map<string, any[]>();
        
        // Get exam items to map videos to strokes
        const examItemsResponse = await fetch(
          `/api/practical-exam/exam-items?sport=${sport}&examType=${examType}&playerId=${player.id}&userId=${user?.id}`,
          {
            headers: getAuthHeaders(),
          }
        );
        const examItemsData = await examItemsResponse.json();
        
        if (examItemsData.success && examItemsData.data && strokeTypes.length > 0) {
          console.log('[Practical Exam] Available stroke codes in strokeTypes:', strokeTypes.map(s => s.code).slice(0, 20).join(', '), '... (total:', strokeTypes.length, ')');
          
          // Group videos by stroke code (all attempts)
          const videosByStroke: { [key: string]: any[] } = {};
          
          // For each uploaded video (in selected period), try to match it to a stroke code
          videosInPeriod.forEach((video: any) => {
            console.log('[Practical Exam] Processing video:', {
              id: video.id,
              filename: video.video_filename,
              stroke_code: video.stroke_code,
              exam_item_id: video.exam_item_id,
            });
            
            // Get stroke code from stroke_code column (player_notes is deprecated and not used)
            const strokeCode: string | null = video.stroke_code || null;
            
            // If we have stroke code from metadata, use it directly
            if (strokeCode) {
              // Trim and normalize stroke code (remove whitespace, ensure uppercase)
              const normalizedStrokeCode = String(strokeCode).trim().toUpperCase();
              
              // Try exact match first (case-insensitive)
              let matchingStroke = strokeTypes.find((stroke: any) => 
                String(stroke.code).trim().toUpperCase() === normalizedStrokeCode
              );
              
              // If no match, try case-insensitive partial match
              if (!matchingStroke) {
                matchingStroke = strokeTypes.find((stroke: any) => 
                  String(stroke.code).trim().toUpperCase().includes(normalizedStrokeCode) ||
                  normalizedStrokeCode.includes(String(stroke.code).trim().toUpperCase())
                );
              }
              
              if (matchingStroke) {
                // Map verification_status to status format expected by frontend
                // Use 'verified' instead of 'approved' for better clarity
                const status = video.verification_status === 'approved' ? 'verified' :
                              video.verification_status === 'rejected' ? 'rejected' :
                              video.verification_status === 'needs_revision' ? 'needs_revision' :
                              'pending';
                
                const videoData = {
                  filename: video.video_filename,
                  videoId: video.id,
                  videoUrl: video.video_url,
                  uploadedAt: video.uploaded_at,
                  status: status,
                  rating: video.rating ? Number(video.rating) : null,
                  adminFeedback: video.admin_feedback || null,
                  ratingNotes: video.rating_notes || null,
                  strokeCode: video.stroke_code,
                  id: video.id,
                  url: video.video_url,
                  uploadAttemptNumber: video.upload_attempt_number || 1,
                  isLatestAttempt: video.is_latest_attempt || false,
                };
                
                // Group by stroke code
                if (!videosByStroke[matchingStroke.code]) {
                  videosByStroke[matchingStroke.code] = [];
                }
                videosByStroke[matchingStroke.code].push(videoData);
                console.log('[Practical Exam] ✅ Mapped video to stroke:', strokeCode, '->', matchingStroke.code, 'video:', video.video_filename, 'status:', status, 'rating:', video.rating, 'attempt:', video.upload_attempt_number);
                return;
              } else {
                console.log('[Practical Exam] ⚠️ Stroke code', strokeCode, '(normalized:', normalizedStrokeCode, ') not found in available strokes.');
                console.log('[Practical Exam] Available stroke codes:', strokeTypes.slice(0, 20).map(s => `'${s.code}'`).join(', '), '...');
                console.log('[Practical Exam] Total strokes available:', strokeTypes.length);
                // Check if BSB001 or BSB004 specifically exist
                if (normalizedStrokeCode === 'BSB001' || normalizedStrokeCode === 'BSB004') {
                  const bsb001Exists = strokeTypes.some(s => String(s.code).trim().toUpperCase() === 'BSB001');
                  const bsb004Exists = strokeTypes.some(s => String(s.code).trim().toUpperCase() === 'BSB004');
                  console.log('[Practical Exam] 🔍 BSB001 exists in strokeTypes:', bsb001Exists);
                  console.log('[Practical Exam] 🔍 BSB004 exists in strokeTypes:', bsb004Exists);
                  if (!bsb001Exists && normalizedStrokeCode === 'BSB001') {
                    console.log('[Practical Exam] ❌ BSB001 NOT FOUND in strokeTypes array!');
                  }
                  if (!bsb004Exists && normalizedStrokeCode === 'BSB004') {
                    console.log('[Practical Exam] ❌ BSB004 NOT FOUND in strokeTypes array!');
                  }
                }
              }
            } else {
              console.log('[Practical Exam] ⚠️ No stroke code found for video:', video.id, 'filename:', video.video_filename);
            }
            
            // Fallback: Try to match by exam item name or ID
            const examItem = examItemsData.data.find((item: any) => item.id === video.exam_item_id);
            
            if (examItem) {
              console.log('[Practical Exam] Trying to match by exam item:', examItem.name, 'for video:', video.video_filename);
              
              // Try to find matching stroke by code, stroke type, or shot type
              const matchingStroke = strokeTypes.find((stroke: any) => {
                // Try exact code match in exam item name
                const codeMatch = examItem.name.toLowerCase().includes(stroke.code.toLowerCase());
                // Try stroke type match
                const strokeTypeMatch = stroke.strokeType && examItem.name.toLowerCase().includes(stroke.strokeType.toLowerCase());
                // Try shot type match
                const shotTypeMatch = stroke.shotType && examItem.name.toLowerCase().includes(stroke.shotType.toLowerCase());
                
                return codeMatch || strokeTypeMatch || shotTypeMatch;
              });
              
              if (matchingStroke) {
                // Map verification_status to status format expected by frontend
                // Use 'verified' instead of 'approved' for better clarity
                const status = video.verification_status === 'approved' ? 'verified' :
                              video.verification_status === 'rejected' ? 'rejected' :
                              video.verification_status === 'needs_revision' ? 'needs_revision' :
                              'pending';
                
                const videoData = {
                  filename: video.video_filename,
                  videoId: video.id,
                  videoUrl: video.video_url,
                  uploadedAt: video.uploaded_at,
                  status: status,
                  rating: video.rating ? Number(video.rating) : null,
                  adminFeedback: video.admin_feedback || null,
                  ratingNotes: video.rating_notes || null,
                  strokeCode: video.stroke_code,
                  id: video.id,
                  url: video.video_url,
                  uploadAttemptNumber: video.upload_attempt_number || 1,
                  isLatestAttempt: video.is_latest_attempt || false,
                };
                
                // Group by stroke code
                if (!videosByStroke[matchingStroke.code]) {
                  videosByStroke[matchingStroke.code] = [];
                }
                videosByStroke[matchingStroke.code].push(videoData);
                console.log('[Practical Exam] ✅ Mapped video to stroke (by exam item):', matchingStroke.code, 'video:', video.video_filename, 'status:', status, 'rating:', video.rating, 'attempt:', video.upload_attempt_number);
              } else {
                console.log('[Practical Exam] ⚠️ Could not match video to stroke. Exam item:', examItem.name, 'Video ID:', video.id);
                // Log all available strokes for debugging
                console.log('[Practical Exam] Available stroke codes:', strokeTypes.slice(0, 20).map(s => s.code).join(', '), '...');
              }
            } else {
              console.log('[Practical Exam] ⚠️ Exam item not found for video. Exam item ID:', video.exam_item_id, 'Video ID:', video.id);
            }
          });
          
          // Convert videosByStroke object to videosMap Map
          // Store ALL attempts per stroke code (up to 3) for proper scoring and display
          Object.keys(videosByStroke).forEach(strokeCode => {
            const videos = videosByStroke[strokeCode];
            // Sort by attempt number (1, 2, 3) to show in order
            const sortedVideos = videos.sort((a, b) => (a.uploadAttemptNumber || 1) - (b.uploadAttemptNumber || 1));
            videosMap.set(strokeCode, sortedVideos);
            console.log('[Practical Exam] Setting', sortedVideos.length, 'video attempts for stroke:', strokeCode);
          });
        }
        
        setUploadedVideos(videosMap);
        console.log('[Practical Exam] ✅ Loaded', videosMap.size, 'uploaded videos mapped to strokes');
        console.log('[Practical Exam] Mapped stroke codes:', Array.from(videosMap.keys()).join(', '));
      } else {
        console.log('[Practical Exam] No videos found or API error:', data);
        setUploadedVideos(new Map());
      }
    } catch (error) {
      console.error('[Practical Exam] Error loading uploaded videos:', error);
      // Don't show error to user, just log it
      setUploadedVideos(new Map());
    }
  };

  const getRatingChangeSummary = () => {
    const trackedCodes = new Set<string>([
      ...Object.keys(savedRatings || {}),
      ...Object.keys(ratings || {}),
    ]);

    let increased = 0;
    let decreased = 0;
    let same = 0;

    trackedCodes.forEach((code) => {
      const previous = Number(savedRatings?.[code] || 0);
      const current = Number(ratings?.[code] || 0);
      if (previous <= 0 && current <= 0) return;
      if (current > previous) increased++;
      else if (current < previous) decreased++;
      else same++;
    });

    return { increased, decreased, same };
  };

  const loadSkillTrend = async (strokeCode: string) => {
    if (!player?.id || !strokeCode || !selectedSport || !categoryId) return;
    if (Array.isArray(skillTrendByCode[strokeCode]) && skillTrendByCode[strokeCode].length > 1) return;
    if (loadingTrendCodes.has(strokeCode)) return;
    try {
      setLoadingTrendCodes((prev) => new Set(prev).add(strokeCode));
      const params = new URLSearchParams({
        playerId: String(player.id),
        categoryId: String(categoryId),
        sport: String(selectedSport),
        code: String(strokeCode),
        limit: '24',
      });
      const response = await fetch(`/api/ranking-reports/skill-trend?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data?.success || !Array.isArray(data?.data)) {
        return;
      }
      const trend = data.data.map((row: any) => ({
        rating: Number(row.rating || 0),
        label: row.periodKey || new Date(row.updatedAt || row.periodEndDate || Date.now()).toLocaleDateString(),
        at: row.updatedAt || row.periodEndDate,
      }));
      setSkillTrendByCode((prev) => ({ ...prev, [strokeCode]: trend }));
    } catch (error) {
      console.error('Error loading ranking skill trend:', error);
    } finally {
      setLoadingTrendCodes((prev) => {
        const next = new Set(prev);
        next.delete(strokeCode);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!player?.id || strokeTypes.length === 0) return;
    strokeTypes.slice(0, 60).forEach((stroke) => {
      const strokeCode = stroke?.code;
      const previous = Number(strokeCode ? savedRatings?.[strokeCode] || 0 : 0);
      const current = Number(strokeCode ? ratings?.[strokeCode] || 0 : 0);
      if (strokeCode && (previous > 0 || current > 0)) {
        void loadSkillTrend(strokeCode);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, selectedSport, categoryId, strokeTypes.map((stroke) => stroke.code).join('|'), ratings, savedRatings]);

  const getTrendDirectionForStroke = (strokeCode: string) => {
    const previous = Number(savedRatings?.[strokeCode] || 0);
    const current = Number(ratings?.[strokeCode] || 0);
    if (previous <= 0 && current <= 0) return 'same' as const;
    if (current > previous) return 'up' as const;
    if (current < previous) return 'down' as const;
    return 'same' as const;
  };

  const loadEvaluationHistory = async () => {
    if (!player?.id) return;
    try {
      // v2: list submitted reports for this player (+ filter by sport/category)
      const qs = new URLSearchParams({
        sport: String(selectedSport),
        categoryId: String(categoryId),
      });
      const response = await fetch(`/api/ranking-reports/player/${player.id}?${qs.toString()}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!data.success) return;

      const history = (data.data || []).map((r: any) => ({
        periodKey: r.periodKey,
        periodEndDate: r.periodEndDate,
        reportId: r.reportId,
        submittedAt: r.submittedAt,
        totalScore: Number(r.score || 0),
        maxScore: Number(r.maxScore || 0),
      }));
      setEvaluationHistory(history);
    } catch (err: any) {
      console.error('Error loading evaluation history:', err);
    }
  };

  const calculateScores = () => {
    if (strokeTypes.length === 0) return;

    let total = 0;
    let ratedCount = 0; // Count only rated strokes
    let backcourt = 0, backcourtMax = 0;
    let midcourt = 0, midcourtMax = 0;
    let frontcourt = 0, frontcourtMax = 0;

    strokeTypes.forEach((stroke) => {
      let rating = ratings[stroke.code] || 0;
      
      // For practical test, use video evaluation rating if available
      // Use BEST rating from all attempts (up to 3), only from verified/approved videos
      if (isPracticalTest && uploadedVideos instanceof Map) {
        const attempts = uploadedVideos.get(stroke.code);
        if (attempts) {
          const videoArray = Array.isArray(attempts) ? attempts : [attempts];
          if (videoArray.length > 0) {
            // Get all verified/approved attempts
            const verifiedAttempts = videoArray.filter((v: any) => 
              (v.status === 'verified' || v.status === 'approved' || v.status === 'validated') &&
              v.rating !== null && v.rating !== undefined
            );
            if (verifiedAttempts.length > 0) {
              // Use the HIGHEST rating from all verified attempts (best of 3)
              const bestRating = Math.max(...verifiedAttempts.map((v: any) => Number(v.rating) || 0));
              rating = bestRating;
            } else {
              rating = 0; // No verified videos with ratings
            }
          } else {
            rating = 0; // No video uploaded
          }
        } else {
          rating = 0; // No video uploaded
        }
      }
      
      total += rating; // Accumulated score (includes all strokes, even unrated = 0)
      
      if (rating > 0) {
        // Count only rated strokes for rated-only score
        ratedCount++;
      }
      
      const position = (stroke.courtPosition || '').toLowerCase();
      if (position.includes('backcourt') || position.includes('back court')) {
        backcourt += rating;
        if (rating > 0) {
          backcourtMax += 5;
        }
      } else if (position.includes('midcourt') || position.includes('mid court')) {
        midcourt += rating;
        if (rating > 0) {
          midcourtMax += 5;
        }
      } else if (position.includes('frontcourt') || position.includes('front court')) {
        frontcourt += rating;
        if (rating > 0) {
          frontcourtMax += 5;
        }
      }
    });

    // Accumulated max score: all 241 skills * 5 (original calculation)
    const accumulatedMax = strokeTypes.length * 5;
    
    // Rated-only max score: only rated strokes * 5 (new calculation)
    const ratedOnlyMax = ratedCount * 5;
    
    // Rated-only score: sum of only rated strokes
    let ratedOnlyTotal = 0;
    strokeTypes.forEach((stroke) => {
      let rating = ratings[stroke.code] || 0;
      
      // For practical test, use video evaluation rating if available
      // Use BEST rating from all attempts (up to 3), only from verified/approved videos
      if (isPracticalTest && uploadedVideos instanceof Map) {
        const attempts = uploadedVideos.get(stroke.code);
        if (attempts) {
          const videoArray = Array.isArray(attempts) ? attempts : [attempts];
          if (videoArray.length > 0) {
            // Get all verified/approved attempts
            const verifiedAttempts = videoArray.filter((v: any) => 
              (v.status === 'verified' || v.status === 'approved' || v.status === 'validated') &&
              v.rating !== null && v.rating !== undefined
            );
            if (verifiedAttempts.length > 0) {
              // Use the HIGHEST rating from all verified attempts (best of 3)
              const bestRating = Math.max(...verifiedAttempts.map((v: any) => Number(v.rating) || 0));
              rating = bestRating;
            } else {
              rating = 0; // No verified videos with ratings
            }
          } else {
            rating = 0; // No video uploaded
          }
        } else {
          rating = 0; // No video uploaded
        }
      }
      
      if (rating > 0) {
        ratedOnlyTotal += rating;
      }
    });

    setTotalScore(total);
    setMaxScore(accumulatedMax); // Original: all 241 skills * 5
    setRatedScore(ratedOnlyTotal); // New: only rated strokes sum
    setRatedMaxScore(ratedOnlyMax); // New: only rated strokes * 5
    setBackcourtScore(backcourt);
    setBackcourtMax(backcourtMax);
    setMidcourtScore(midcourt);
    setMidcourtMax(midcourtMax);
    setFrontcourtScore(frontcourt);
    setFrontcourtMax(frontcourtMax);
  };
  
  // Recalculate scores when uploadedVideos or ratings change (for practical test)
  useEffect(() => {
    calculateScores();
  }, [ratings, strokeTypes, uploadedVideos, isPracticalTest]);

  const rankingFeedback = useMemo(() => {
    const trackedCodes = new Set<string>([
      ...Object.keys(savedRatings || {}),
      ...Object.keys(ratings || {}),
    ]);

    let increased = 0;
    let decreased = 0;
    let same = 0;
    const changedItems: Array<{ code: string; label: string; previous: number; current: number; direction: 'up' | 'down' }> = [];

    trackedCodes.forEach((code) => {
      const previous = Number(savedRatings?.[code] || 0);
      const current = Number(ratings?.[code] || 0);
      if (previous <= 0 && current <= 0) return;
      if (current > previous) {
        increased++;
        const stroke = allStrokeTypes.find((s) => s.code === code);
        changedItems.push({
          code,
          label: stroke?.code || stroke?.strokeType || code,
          previous,
          current,
          direction: 'up',
        });
      } else if (current < previous) {
        decreased++;
        const stroke = allStrokeTypes.find((s) => s.code === code);
        changedItems.push({
          code,
          label: stroke?.code || stroke?.strokeType || code,
          previous,
          current,
          direction: 'down',
        });
      } else {
        same++;
      }
    });

    const comparableCodes = Array.from(trackedCodes).filter((code) => {
      const previous = Number(savedRatings?.[code] || 0);
      const current = Number(ratings?.[code] || 0);
      return previous > 0 || current > 0;
    });
    const previousComparableScore = comparableCodes.reduce((sum, code) => sum + Number(savedRatings?.[code] || 0), 0);
    const currentComparableScore = comparableCodes.reduce((sum, code) => sum + Number(ratings?.[code] || 0), 0);
    const maxPossibleScore = strokeTypes.length * 5;
    const currentTotalScore = Object.values(ratings || {}).reduce((sum, val) => sum + Number(val || 0), 0);
    const improvementRatePct = previousComparableScore > 0
      ? ((currentComparableScore - previousComparableScore) / previousComparableScore) * 100
      : (currentTotalScore > 0 ? 100 : 0);
    const totalScorePct = maxPossibleScore > 0 ? (currentTotalScore / maxPossibleScore) * 100 : 0;

    const sortedHistory = [...evaluationHistory].sort(
      (a: any, b: any) => new Date(a.submittedAt || a.periodEndDate || 0).getTime() - new Date(b.submittedAt || b.periodEndDate || 0).getTime()
    );
    const latest = sortedHistory[sortedHistory.length - 1];
    const prev = sortedHistory.length > 1 ? sortedHistory[sortedHistory.length - 2] : null;
    const daysSincePreviousSave =
      latest && prev
        ? Math.max(
            0,
            Math.round(
              (new Date(latest.submittedAt || latest.periodEndDate).getTime() -
                new Date(prev.submittedAt || prev.periodEndDate).getTime()) /
                (1000 * 60 * 60 * 24)
            )
          )
        : null;

    let totalScoreTrendData = sortedHistory
      .map((h: any) => {
        const score = Number(h.totalScore || 0);
        const max = Number(h.maxScore || 0);
        return {
          label: h.periodKey || new Date(h.submittedAt || h.periodEndDate || Date.now()).toLocaleDateString(),
          scorePct: max > 0 ? (score / max) * 100 : 0,
        };
      })
      .slice(-12);

    if (totalScoreTrendData.length === 0 && (previousComparableScore > 0 || currentComparableScore > 0)) {
      totalScoreTrendData = [
        { label: 'Prev', scorePct: maxPossibleScore > 0 ? (previousComparableScore / maxPossibleScore) * 100 : 0 },
        { label: 'Now', scorePct: maxPossibleScore > 0 ? (currentComparableScore / maxPossibleScore) * 100 : 0 },
      ];
    }

    return {
      increased,
      decreased,
      same,
      changedItems,
      improvedItems: changedItems.filter((c) => c.direction === 'up'),
      improvementRatePct,
      totalScorePct,
      currentTotalScore,
      maxPossibleScore,
      currentSavedAt: latest?.submittedAt || latest?.periodEndDate || null,
      daysSincePreviousSave,
      totalScoreTrendData,
    };
  }, [savedRatings, ratings, allStrokeTypes, strokeTypes.length, evaluationHistory]);

  // Keep feedback panel live while edits are pending.
  const feedbackForPanel = ratingsDirty ? rankingFeedback : (lastSavedRankingFeedback || rankingFeedback);


  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      // TODO: Implement video upload API
      const formData = new FormData();
      formData.append('video', file);
      formData.append('playerId', player.id);
      formData.append('categoryId', categoryId);

      const token = getAuthToken();
      const response = await fetch('/api/ranking-evaluations/upload-video', {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.ok) {
        // Handle success
        alert('Video uploaded successfully');
      } else {
        setError('Failed to upload video');
      }
    } catch (err: any) {
      console.error('Error uploading video:', err);
      setError(err.message || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Handle per-stroke video upload
  const handleStrokeVideoUpload = async (strokeCode: string, strokeType: any, file: File, attemptNumber: number = 1) => {
    const uploadKey = `${strokeCode}-${attemptNumber}`;
    setUploadingStrokes(prev => new Set(prev).add(uploadKey));
    setError(null);
    
    try {
      // Check if evaluation period is selected
      if (!selectedPeriodKey) {
        throw new Error('Please select an Evaluation Period before uploading videos.');
      }
      
      // Check if user is available
      if (!user?.id) {
        throw new Error('User not logged in. Please refresh the page and try again.');
      }
      
      // Get exam items to find matching item for this stroke
      const token = getAuthToken();
      const sport = selectedSport || 'Badminton';
      const examType = 'stroke-shot';
      
      // First, get exam items
      const examItemsResponse = await fetch(
        `/api/practical-exam/exam-items?sport=${sport}&examType=${examType}&playerId=${player.id}&userId=${user?.id}`,
        {
          headers: getAuthHeaders(),
        }
      );
      
      const examItemsData = await examItemsResponse.json();
      
      // Log for debugging
      console.log('Exam items response:', {
        success: examItemsData.success,
        dataLength: examItemsData.data?.length || 0,
        data: examItemsData.data,
        error: examItemsData.message,
      });
      
      if (!examItemsData.success) {
        console.error('Failed to load exam items:', examItemsData.message);
        throw new Error(`Failed to load exam items: ${examItemsData.message || 'Unknown error'}`);
      }
      
      // Check if we have any exam items
      if (!examItemsData.data || examItemsData.data.length === 0) {
        console.warn('No exam items found. Backend should create a default item, but if this persists, contact admin.');
        // Wait a moment and retry once (backend might be creating default item)
        await new Promise(resolve => setTimeout(resolve, 500));
        const retryResponse = await fetch(
          `/api/practical-exam/exam-items?sport=${sport}&examType=${examType}&playerId=${player.id}&userId=${user?.id}`,
          { headers: getAuthHeaders() }
        );
        const retryData = await retryResponse.json();
        
        if (!retryData.success || !retryData.data || retryData.data.length === 0) {
          throw new Error(
            `No exam items found for ${sport} - ${examType}. ` +
            `Please contact admin to create exam items. ` +
            `Admin can run migration: backend/migrations/002_create_practical_exam_items_from_strokes.sql ` +
            `or create items in Admin Center → Assessment Skills with assessment_type = '${examType}'.`
          );
        }
        
        // Use retry data
        examItemsData.data = retryData.data;
      }
      
      // Try to find matching exam item by stroke code or name
      // Match by: code, stroke type + shot type, or item name
      const examItem = examItemsData.data.find((item: any) => 
        item.name.toLowerCase().includes(strokeCode.toLowerCase()) ||
        item.name.toLowerCase().includes((strokeType.strokeType || '').toLowerCase()) ||
        item.name.toLowerCase().includes((strokeType.shotType || '').toLowerCase())
      ) || examItemsData.data[0]; // Fallback to first item if no match
      
      if (!examItem) {
        throw new Error('No matching exam item found for this stroke.');
      }
      
      console.log('Selected exam item:', examItem);
      
      // Get video limits
      const limitsResponse = await fetch(
        `/api/practical-exam/video-limits?sport=${sport}&examType=${examType}&examItemId=${examItem.id}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const limitsData = await limitsResponse.json();
      const limits = limitsData.success ? limitsData.data : {
        maxFileSizeMB: 250,
        maxDurationSeconds: 60,
        minDurationSeconds: 5,
      };
      
      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > limits.maxFileSizeMB) {
        throw new Error(`File size (${fileSizeMB.toFixed(2)}MB) exceeds maximum allowed (${limits.maxFileSizeMB}MB)`);
      }
      
      // Initiate upload
      const initiateResponse = await fetch(`/api/practical-exam/videos/upload?userId=${user?.id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sport,
          examType,
          examItemId: examItem.id,
          filename: file.name,
          fileSize: file.size,
          contentType: file.type,
          estimatedDuration: 0, // Will be validated on completion
          userId: user?.id, // Also include in body as fallback
          strokeCode: strokeCode, // Pass stroke code for matching
          uploadAttemptNumber: attemptNumber, // Pass attempt number
        }),
      });
      
      const initiateData = await initiateResponse.json();
      if (!initiateData.success) {
        throw new Error(initiateData.message || 'Failed to initiate upload');
      }
      
      const { preSignedUrl, s3Key, uploadId } = initiateData.data;
      
      // Upload to S3
      const uploadResponse = await fetch(preSignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload video to storage');
      }
      
      // Complete upload
      const completeResponse = await fetch(`/api/practical-exam/videos/complete-upload?userId=${user?.id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          uploadId,
          s3Key,
          videoDuration: 0, // TODO: Extract duration if possible
          fileSize: file.size,
          userId: user?.id, // Also include in body as fallback
          strokeCode: strokeCode, // Pass stroke code for storage
        }),
      });
      
      const completeData = await completeResponse.json();
      if (!completeData.success) {
        throw new Error(completeData.message || 'Failed to complete upload');
      }
      
      // Update state with timestamp
      setUploadedVideos(prev => new Map(prev).set(strokeCode, {
        filename: file.name,
        videoId: completeData.data.videoId,
        videoUrl: completeData.data.videoUrl,
        uploadedAt: new Date().toISOString(), // Store upload timestamp
      }));
      
      setSuccess(`Video uploaded successfully for stroke ${strokeCode}`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Reload videos to ensure persistence
      if (selectedPeriodKey) {
        setTimeout(() => {
          loadUploadedVideos();
        }, 1000);
      }
      
    } catch (err: any) {
      console.error('Error uploading stroke video:', err);
      setError(err.message || `Failed to upload video for stroke ${strokeCode}`);
    } finally {
      setUploadingStrokes(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadKey);
        return newSet;
      });
    }
  };

  // Handle bulk row upload
  const handleBulkRowUpload = async (rowId: string, file: File, strokes: any[]) => {
    setBulkUploadRows(prev => prev.map(r => 
      r.id === rowId ? { ...r, uploading: true } : r
    ));
    setError(null);
    
    try {
      // Check if user is available
      if (!user?.id) {
        throw new Error('User not logged in. Please refresh the page and try again.');
      }
      
      const token = getAuthToken();
      const sport = selectedSport || 'Badminton';
      const examType = 'stroke-shot';
      
      // Get exam items
      const examItemsResponse = await fetch(
        `/api/practical-exam/exam-items?sport=${sport}&examType=${examType}&playerId=${player.id}&userId=${user?.id}`,
        {
          headers: getAuthHeaders(),
        }
      );
      
      const examItemsData = await examItemsResponse.json();
      if (!examItemsData.success || examItemsData.data.length === 0) {
        throw new Error('No exam items available. Please contact admin.');
      }
      
      // Upload video for each stroke in the row
      const uploadPromises = strokes.map(async (stroke) => {
        // Find matching exam item
        const examItem = examItemsData.data.find((item: any) => 
          item.name.toLowerCase().includes(stroke.code.toLowerCase()) ||
          item.name.toLowerCase().includes((stroke.strokeType || '').toLowerCase())
        ) || examItemsData.data[0];
        
        // Get limits
        const limitsResponse = await fetch(
          `/api/practical-exam/video-limits?sport=${sport}&examType=${examType}&examItemId=${examItem.id}`,
          { headers: getAuthHeaders() }
        );
        const limitsData = await limitsResponse.json();
        const limits = limitsData.success ? limitsData.data : {
          maxFileSizeMB: 250,
          maxDurationSeconds: 60,
          minDurationSeconds: 5,
        };
        
        // Validate file size
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > limits.maxFileSizeMB) {
          throw new Error(`File size exceeds limit for ${stroke.code}`);
        }
        
        // Initiate upload
        const initiateResponse = await fetch(`/api/practical-exam/videos/upload?userId=${user?.id}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            sport,
            examType,
            examItemId: examItem.id,
            filename: `${stroke.code}_${file.name}`,
            fileSize: file.size,
            contentType: file.type,
            estimatedDuration: 0,
            userId: user?.id, // Also include in body as fallback
            strokeCode: stroke.code, // Pass stroke code for matching
          }),
        });
        
        const initiateData = await initiateResponse.json();
        if (!initiateData.success) {
          throw new Error(`Failed to initiate upload for ${stroke.code}`);
        }
        
        const { preSignedUrl, s3Key, uploadId } = initiateData.data;
        
        // Upload to S3
        const uploadResponse = await fetch(preSignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${stroke.code}`);
        }
        
        // Complete upload
        const completeResponse = await fetch(`/api/practical-exam/videos/complete-upload?userId=${user?.id}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            uploadId,
            s3Key,
            videoDuration: 0,
            fileSize: file.size,
            userId: user?.id, // Also include in body as fallback
            strokeCode: stroke.code, // Pass stroke code for storage
          }),
        });
        
        const completeData = await completeResponse.json();
        if (!completeData.success) {
          throw new Error(`Failed to complete upload for ${stroke.code}`);
        }
        
        return { strokeCode: stroke.code, success: true, videoId: completeData.data.videoId, videoUrl: completeData.data.videoUrl };
      });
      
      const results = await Promise.allSettled(uploadPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (successful > 0) {
        setSuccess(`Successfully uploaded videos for ${successful} stroke(s)`);
        setTimeout(() => setSuccess(null), 3000);
      }
      
      if (failed > 0) {
        setError(`Failed to upload videos for ${failed} stroke(s)`);
      }
      
      // Update uploaded videos state
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          setUploadedVideos(prev => new Map(prev).set(result.value.strokeCode, {
            filename: file.name,
            videoId: result.value.videoId,
            videoUrl: result.value.videoUrl,
            uploadedAt: new Date().toISOString(),
          }));
        }
      });
      
      // Reload videos to ensure persistence
      if (selectedPeriodKey && successful > 0) {
        setTimeout(() => {
          loadUploadedVideos();
        }, 1000);
      }
      
    } catch (err: any) {
      console.error('Error uploading bulk videos:', err);
      setError(err.message || 'Failed to upload videos');
    } finally {
      setBulkUploadRows(prev => prev.map(r => 
        r.id === rowId ? { ...r, uploading: false } : r
      ));
    }
  };

  const handleRatingChange = (strokeCode: string, rating: number | null) => {
    if (rating === null) {
      // Clicking the same selected star should decrement one step (4->3->2->1->0).
      const currentDisplayed = Number(getDisplayRatingForStroke(strokeCode) || 0);
      const nextRating = Math.max(0, currentDisplayed - 1);
      setRatings(prev => ({
        ...prev,
        [strokeCode]: nextRating,
      }));
      setRatingsDirty(true);
      return;
    }

    // Update local state; auto-save runs in a debounced effect
    setRatings(prev => ({
      ...prev,
      [strokeCode]: rating,
    }));
    setRatingsDirty(true);
  };

  // Bulk rating handlers
  const handleSelectStroke = (strokeCode: string, checked: boolean) => {
    setSelectedStrokes(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(strokeCode);
      } else {
        newSet.delete(strokeCode);
      }
      return newSet;
    });
  };

  const handleSelectAllStrokes = (checked: boolean) => {
    if (checked) {
      // Select all filtered strokes
      const selectableStrokes = strokeTypes.map(st => st.code);
      setSelectedStrokes(new Set(selectableStrokes));
    } else {
      setSelectedStrokes(new Set());
    }
  };

  const handleApplyBulkRating = () => {
    if (bulkRating === 0 || selectedStrokes.size === 0) return;
    
    setRatings(prev => {
      const newRatings = { ...prev };
      selectedStrokes.forEach(code => {
        newRatings[code] = bulkRating;
      });
      return newRatings;
    });
    setRatingsDirty(true);
    
    // Clear selection after applying
    setSelectedStrokes(new Set());
    setBulkRating(0);
  };

  // Get filtered strokes for bulk selection (respects current filters)
  const getFilteredStrokes = () => {
    return strokeTypes.filter(stroke => {
      if (filterCourtPosition && stroke.courtPosition !== filterCourtPosition) return false;
      if (filterHalfSide) {
        const halfSide = stroke.halfSide || '';
        if (filterHalfSide === 'Right Side' && !halfSide.toLowerCase().includes('right')) return false;
        if (filterHalfSide === 'Left Side' && !halfSide.toLowerCase().includes('left')) return false;
      }
      if (filterStrokeType && stroke.strokeType !== filterStrokeType) return false;
      if (filterPlayType && stroke.playType !== filterPlayType) return false;
      if (filterShotType && stroke.shotType !== filterShotType) return false;
      if (filterDifficulty && stroke.difficulty !== filterDifficulty) return false;
      return true;
    });
  };

  const isAllStrokesSelected = () => {
    const filtered = getFilteredStrokes();
    return filtered.length > 0 && filtered.every(st => selectedStrokes.has(st.code));
  };

  const isStrokesIndeterminate = () => {
    const filtered = getFilteredStrokes();
    const selectedCount = filtered.filter(st => selectedStrokes.has(st.code)).length;
    return selectedCount > 0 && selectedCount < filtered.length;
  };

  const handleSaveAll = async (options?: { silent?: boolean }) => {
    if (!selectedPeriodKey) {
      setError('Please select an evaluation period first');
      return;
    }

    if (Object.keys(ratings).length === 0) {
      setError('Please rate at least one stroke type');
      return;
    }

    const previousSnapshot = { ...savedRatings };
    const changeSummary = getRatingChangeSummary();

    setSaving(true);
    setError(null);
    try {
      const periodKey = selectedPeriodKey;
      const periodEndDate = getPeriodEndDateFromKey(periodKey);
      const entries = Object.entries(ratings)
        .filter(([_, rating]) => rating > 0)
        .map(([strokeCode, rating]) => {
          const strokeType = allStrokeTypes.find(st => st.code === strokeCode);
          if (!strokeType) return null;
          return {
            code: strokeType.code,
            rating: rating,
          };
        })
        .filter(entry => entry !== null);

      const response = await fetch('/api/ranking-reports', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          playerId: player.id,
          categoryId,
          sport: selectedSport,
          periodKey,
          periodEndDate,
          entries: entries,
          submitted: false,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const trackedCodes = new Set<string>([
            ...Object.keys(previousSnapshot || {}),
            ...Object.keys(ratings || {}),
          ]);
          const changedItems: Array<{ code: string; label: string; previous: number; current: number; direction: 'up' | 'down' }> = [];
          trackedCodes.forEach((code) => {
            const previous = Number(previousSnapshot?.[code] || 0);
            const current = Number(ratings?.[code] || 0);
            if (previous <= 0 && current <= 0) return;
            if (current === previous) return;
            const stroke = allStrokeTypes.find((s) => s.code === code);
            changedItems.push({
              code,
              label: stroke?.code || stroke?.strokeType || code,
              previous,
              current,
              direction: current > previous ? 'up' : 'down',
            });
          });
          const comparableCodes = Array.from(trackedCodes).filter((code) => {
            const previous = Number(previousSnapshot?.[code] || 0);
            const current = Number(ratings?.[code] || 0);
            return previous > 0 || current > 0;
          });
          const previousComparableScore = comparableCodes.reduce((sum, code) => sum + Number(previousSnapshot?.[code] || 0), 0);
          const currentComparableScore = comparableCodes.reduce((sum, code) => sum + Number(ratings?.[code] || 0), 0);
          const maxPossibleScore = strokeTypes.length * 5;
          const currentTotalScore = Object.values(ratings || {}).reduce((sum, val) => sum + Number(val || 0), 0);
          const improvementRatePct = previousComparableScore > 0
            ? ((currentComparableScore - previousComparableScore) / previousComparableScore) * 100
            : (currentTotalScore > 0 ? 100 : 0);
          const totalScorePct = maxPossibleScore > 0 ? (currentTotalScore / maxPossibleScore) * 100 : 0;
          const sortedHistory = [...evaluationHistory].sort(
            (a: any, b: any) => new Date(a.submittedAt || a.periodEndDate || 0).getTime() - new Date(b.submittedAt || b.periodEndDate || 0).getTime()
          );
          const latest = sortedHistory[sortedHistory.length - 1];
          const prev = sortedHistory.length > 1 ? sortedHistory[sortedHistory.length - 2] : null;
          const daysSincePreviousSave =
            latest && prev
              ? Math.max(
                  0,
                  Math.round(
                    (new Date(latest.submittedAt || latest.periodEndDate).getTime() -
                      new Date(prev.submittedAt || prev.periodEndDate).getTime()) /
                      (1000 * 60 * 60 * 24)
                  )
                )
              : null;
          let totalScoreTrendData = sortedHistory
            .map((h: any) => {
              const score = Number(h.totalScore || 0);
              const max = Number(h.maxScore || 0);
              return {
                label: h.periodKey || new Date(h.submittedAt || h.periodEndDate || Date.now()).toLocaleDateString(),
                scorePct: max > 0 ? (score / max) * 100 : 0,
              };
            })
            .slice(-12);
          if (totalScoreTrendData.length === 0) {
            totalScoreTrendData = [
              { label: 'Prev', scorePct: maxPossibleScore > 0 ? (previousComparableScore / maxPossibleScore) * 100 : 0 },
              { label: 'Now', scorePct: maxPossibleScore > 0 ? (currentComparableScore / maxPossibleScore) * 100 : 0 },
            ];
          } else {
            totalScoreTrendData = [...totalScoreTrendData, { label: 'Now', scorePct: totalScorePct }].slice(-12);
          }
          if (!options?.silent) {
            setLastSavedRankingFeedback({
              increased: changeSummary.increased,
              decreased: changeSummary.decreased,
              same: changeSummary.same,
              changedItems,
              improvedItems: changedItems.filter((c) => c.direction === 'up'),
              improvementRatePct,
              totalScorePct,
              currentTotalScore,
              maxPossibleScore,
              currentSavedAt: new Date().toISOString(),
              daysSincePreviousSave,
              totalScoreTrendData,
            });
          }
          setSkillTrendByCode((prev) => {
            const next = { ...prev };
            Object.entries(ratings || {}).forEach(([code, val]) => {
              const ratingValue = Number(val || 0);
              if (ratingValue <= 0) return;
              const existing = Array.isArray(next[code]) ? [...next[code]] : [];
              existing.push({ rating: ratingValue, label: 'Now', at: new Date().toISOString() });
              next[code] = existing.slice(-12);
            });
            return next;
          });
          setSavedRatings({ ...ratings });
          if (!options?.silent) {
            setSuccess(
              `Ratings saved. Increased: ${changeSummary.increased} | Same: ${changeSummary.same} | Decreased: ${changeSummary.decreased}`
            );
            setTimeout(() => setSuccess(null), 3000);
          }
          if (data.data?.reportId) {
            setCurrentReportId(String(data.data.reportId));
          }
          setRatingsDirty(false);
          loadEvaluationHistory();
        } else {
          setError(data.message || 'Failed to save ratings');
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to save ratings' }));
        setError(errorData.message || 'Failed to save ratings');
      }
    } catch (err: any) {
      console.error('Error saving ratings:', err);
      setError(err.message || 'Failed to save ratings');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!ratingsDirty) return;
    if (!canRate || !isStrokeLikeSubcategory) return;
    if (isPracticalTest) return;
    if (!selectedPeriodKey) return;
    if (Object.keys(ratings).length === 0) return;

    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setTimeout(() => {
      handleSaveAll({ silent: true });
    }, 700);

    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratings, ratingsDirty, canRate, selectedSubcategory, isPracticalTest, selectedPeriodKey]);

  const handleSubmitAll = async () => {
    if (!selectedPeriodKey) {
      setError('Please select an evaluation period first');
      return;
    }

    if (Object.keys(ratings).length === 0) {
      setError('Please rate at least one stroke type');
      return;
    }

    const changeSummary = getRatingChangeSummary();

    setSubmitting(true);
    setError(null);
    try {
      const periodKey = selectedPeriodKey;
      const periodEndDate = getPeriodEndDateFromKey(periodKey);
      const entries = Object.entries(ratings)
        .filter(([_, rating]) => rating > 0)
        .map(([strokeCode, rating]) => {
          const strokeType = allStrokeTypes.find(st => st.code === strokeCode);
          if (!strokeType) return null;
          return {
            code: strokeType.code,
            rating: rating,
          };
        })
        .filter(entry => entry !== null);

      const response = await fetch('/api/ranking-reports', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          playerId: player.id,
          categoryId,
          sport: selectedSport,
          periodKey,
          periodEndDate,
          entries: entries,
          submitted: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccess(
            `Ranking submitted. Increased: ${changeSummary.increased} | Same: ${changeSummary.same} | Decreased: ${changeSummary.decreased}`
          );
          setIsSubmitted(true);
          setSavedRatings({ ...ratings });
          if (data.data?.reportId) {
            setCurrentReportId(String(data.data.reportId));
          }
          setTimeout(() => setSuccess(null), 3000);
          loadEvaluationHistory();
        } else {
          setError(data.message || 'Failed to submit evaluation');
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to submit evaluation' }));
        setError(errorData.message || 'Failed to submit evaluation');
      }
    } catch (err: any) {
      console.error('Error submitting evaluation:', err);
      setError(err.message || 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  // Load coach name
  const loadCoachName = async () => {
    // Coach/Academy view-only uses the linked coach dropdown instead
    if (shouldShowEvaluatedByDropdown) return;

    if (categoryId === 'self-evaluation') {
      setCoachName('Self Evaluation');
      return;
    }

    if (!coachId || coachId === 'self') {
      setCoachName(isCoachAcademy ? 'Coach/Academy' : 'N/A');
      return;
    }
    
    try {
      const response = await fetch(`/api/coaches`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success && data.data) {
        const coach = data.data.find((c: any) => c.id === coachId);
        if (coach && coach.user) {
          setCoachName(`${coach.user.firstName || ''} ${coach.user.lastName || ''}`.trim() || 'Unknown Coach');
        } else {
          setCoachName('Unknown Coach');
        }
      } else {
        setCoachName('Unknown Coach');
      }
    } catch (error) {
      console.error('Error loading coach name:', error);
      setCoachName('Unknown Coach');
    }
  };

  // Load coach name on mount
  useEffect(() => {
    loadCoachName();
  }, [coachId, categoryId]);

  // Generate manual assessment form
  const handleDownloadManualForm = async () => {
    try {
      const displayCoachName = coachName || 'N/A';
      console.log('[RankingEvaluation] Generating manual form...', {
        player: player?.id,
        selectedSport,
        categoryLabel,
        coachName: displayCoachName,
        selectedPeriodKey
      });
      await generateRankingEvaluationManualForm(
        player,
        selectedSport,
        categoryLabel,
        displayCoachName,
        selectedPeriodKey ? getPeriodEndDateFromKey(selectedPeriodKey) : undefined
      );
      console.log('[RankingEvaluation] Manual form generated successfully');
    } catch (error) {
      console.error('Error generating manual form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to generate manual form: ${errorMessage}`);
    }
  };

  // Generate report PDF
  const handleDownloadReport = async () => {
    if (!selectedPeriodKey || !isSubmitted) {
      alert('Please submit the evaluation first before downloading the report.');
      return;
    }

    try {
      // Fetch the stroke types in the correct order (same as UI and manual form)
      const strokeTypesResponse = await fetch(`/api/admin/ranking-stroke-types?sport=${encodeURIComponent(selectedSport)}`, {
        headers: getAuthHeaders()
      });
      if (!strokeTypesResponse.ok) {
        throw new Error('Failed to load stroke types');
      }
      const strokeTypesData = await strokeTypesResponse.json();
      
      if (!strokeTypesData.success || !strokeTypesData.data) {
        throw new Error('No stroke types found');
      }

      // Sort by displayOrder (same as UI and manual form)
      const orderedStrokeTypes = strokeTypesData.data
        .filter((st: any) => st.isActive !== false)
        .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));

      // Fetch report by reportId (v2)
      let reportId = currentReportId;
      if (!reportId) {
        const qs = new URLSearchParams({
          playerId: String(player.id),
          categoryId: String(categoryId),
          sport: String(selectedSport),
          periodKey: String(selectedPeriodKey),
          status: 'submitted',
        });
        const lookupRes = await fetch(`/api/ranking-reports/lookup?${qs.toString()}`, {
          headers: getAuthHeaders()
        });
        const lookupData = await lookupRes.json();
        if (!lookupRes.ok || !lookupData.success || !lookupData.data?.exists || !lookupData.data?.reportId) {
          alert('No submitted report found for this period.');
          return;
        }
        reportId = String(lookupData.data.reportId);
        setCurrentReportId(reportId);
      }

      const reportRes = await fetch(`/api/ranking-reports/${encodeURIComponent(reportId)}`, {
        headers: getAuthHeaders()
      });
      const reportJson = await reportRes.json();
      if (!reportRes.ok || !reportJson.success) {
        alert('Failed to load submitted report for download.');
        return;
      }

      const reportDataV2 = reportJson.data;
      const submittedRatingsMap = new Map<string, any>();
      (reportDataV2.strokes || []).forEach((stroke: any) => {
        submittedRatingsMap.set(stroke.code, stroke);
      });

      // Map strokes in the same order as UI and manual form (by displayOrder)
      const strokes = orderedStrokeTypes.map((strokeType: any) => {
        const submittedData = submittedRatingsMap.get(strokeType.code);
        return {
          code: strokeType.code,
          courtPosition: strokeType.courtPosition || '',
          halfSide: strokeType.halfSide || '',
          strokeType: strokeType.strokeType || '',
          strokeAction: strokeType.strokeAction || '',
          shotType: strokeType.shotType || '',
          placement: strokeType.placement || '',
          rating: submittedData?.rating || 0,
        };
      });

      // Calculate scores from strokes data
      let accumulatedTotal = 0;
      let ratedOnlyTotal = 0;
      let ratedCount = 0;
      let backcourtScore = 0, backcourtMax = 0;
      let midcourtScore = 0, midcourtMax = 0;
      let frontcourtScore = 0, frontcourtMax = 0;

      strokes.forEach((stroke: any) => {
        const rating = stroke.rating || 0;
        accumulatedTotal += rating; // Accumulated: includes all strokes (even 0-rated)
        
        if (rating > 0) {
          ratedCount++;
          ratedOnlyTotal += rating;
        }

        // Calculate coverage scores
        const position = (stroke.courtPosition || '').toLowerCase();
        if (position.includes('backcourt') || position.includes('back court')) {
          backcourtScore += rating;
          if (rating > 0) {
            backcourtMax += 5;
          }
        } else if (position.includes('midcourt') || position.includes('mid court')) {
          midcourtScore += rating;
          if (rating > 0) {
            midcourtMax += 5;
          }
        } else if (position.includes('frontcourt') || position.includes('front court')) {
          frontcourtScore += rating;
          if (rating > 0) {
            frontcourtMax += 5;
          }
        }
      });

      // Accumulated max: all strokes * 5
      const accumulatedMax = strokes.length * 5;
      // Rated-only max: only rated strokes * 5
      const ratedOnlyMax = ratedCount * 5;
      // Accumulated percentage
      const accumulatedPercentage = accumulatedMax > 0 ? (accumulatedTotal / accumulatedMax) * 100 : 0;
      // Rated-only percentage
      const ratedOnlyPercentage = ratedOnlyMax > 0 ? (ratedOnlyTotal / ratedOnlyMax) * 100 : 0;

      // Coverage percentages
      const backcourtPercentage = backcourtMax > 0 ? (backcourtScore / backcourtMax) * 100 : 0;
      const midcourtPercentage = midcourtMax > 0 ? (midcourtScore / midcourtMax) * 100 : 0;
      const frontcourtPercentage = frontcourtMax > 0 ? (frontcourtScore / frontcourtMax) * 100 : 0;

      const report = {
        player,
        categoryId,
        categoryLabel,
        sport: selectedSport,
        evaluationDate: getPeriodEndDateFromKey(selectedPeriodKey),
        strokes,
        totalScore: accumulatedTotal,
        maxScore: accumulatedMax,
        ratedStrokes: ratedCount,
        percentage: accumulatedPercentage,
        // Rated-only scores
        ratedScore: ratedOnlyTotal,
        ratedMaxScore: ratedOnlyMax,
        ratedPercentage: ratedOnlyPercentage,
        // Coverage scores and percentages
        backcourtScore,
        backcourtMax,
        backcourtPercentage,
        midcourtScore,
        midcourtMax,
        midcourtPercentage,
        frontcourtScore,
        frontcourtMax,
        frontcourtPercentage,
        grade: reportDataV2.grade,
        gradeDescription: reportDataV2.gradeDescription,
        coachName: categoryId === 'self-evaluation' ? 'self' : (coachName || 'N/A'),
        submittedAt: reportDataV2.submittedAt || new Date().toISOString(),
      };

      console.log('[RankingEvaluation] Generating report PDF...', {
        player: player?.id,
        categoryId,
        strokesCount: strokes.length,
        totalScore: report.totalScore
      });
      await generateRankingEvaluationReportPDF(report);
      console.log('[RankingEvaluation] Report PDF generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to generate report: ${errorMessage}`);
    }
  };

  const headerCanSave = Boolean(
    canRate && !saving && !submitting && selectedPeriodKey && Object.keys(ratings).length > 0 && !isSubmitted
  );

  const activeSkillItems =
    selectedSubcategory === 'drills' ? drills : selectedSubcategory === 'fitness' ? fitness : strokeTypes;

  useEffect(() => {
    if (!embeddedInEvaluatorTabs || !onEmbeddedStateChange) return;
    const ratedCount = activeSkillItems.filter(
      (st) => ratings[st.code] && ratings[st.code] > 0
    ).length;
    const notRatedCount = Math.max(activeSkillItems.length - ratedCount, 0);
    onEmbeddedStateChange({
      metrics: {
        totalScore,
        maxScore,
        ratedCount,
        notRatedCount,
      },
      canRate,
      canDownloadManual: true,
      canDownloadReport: Boolean(isSubmitted),
      isSaving: saving,
      ratingsDirty,
      isLoading: isStrokeLikeSubcategory ? loading || loadingRatings : loading,
    });
  }, [
    embeddedInEvaluatorTabs,
    onEmbeddedStateChange,
    totalScore,
    maxScore,
    activeSkillItems,
    ratings,
    canRate,
    isSubmitted,
    saving,
    ratingsDirty,
    loading,
    loadingRatings,
    isStrokeLikeSubcategory,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      triggerSave: () => {
        void handleSaveAll();
      },
      triggerDownloadManual: () => {
        void handleDownloadManualForm();
      },
      triggerDownloadReport: () => {
        void handleDownloadReport();
      },
      canSave: headerCanSave,
      canDownloadManual: !isPracticalTest,
      canDownloadReport: Boolean(isSubmitted),
      isSaving: saving,
    }),
    [headerCanSave, isPracticalTest, isSubmitted, saving]
  );

  // Validate player object AFTER all hooks are declared (React Hooks rule)
  if (!player || !player.id || (typeof player.id === 'string' && player.id.trim() === '')) {
    console.error('[RankingEvaluationWithTabs] Missing player.id:', { player, playerId: player?.id });
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Player information is missing. Please ensure you are logged in and have a valid player account.
          {player && <Typography variant="body2" sx={{ mt: 1 }}>Player ID: {player.id || 'undefined'}</Typography>}
        </Alert>
      </Box>
    );
  }

  // Generate dynamic title for Certificate Exam
  const getPageTitle = () => {
    if (categoryId === 'practical-test' && selectedSubcategory && selectedSkillLevel) {
      const subcategoryLabel = selectedSubcategory === 'strokes-shots'
        ? 'Strokes & Shots'
        : selectedSubcategory === 'shooting'
          ? 'Shooting'
          :
                               selectedSubcategory === 'drills' ? 'Drills' : 'Fitness';
      const levelLabel = selectedSkillLevel.charAt(0).toUpperCase() + selectedSkillLevel.slice(1);
      return { main: `Certificate Exam - ${subcategoryLabel}`, level: levelLabel };
    }
    return { main: categoryLabel, level: null };
  };

  const pageTitle = getPageTitle();
  const ratedSkillCount = strokeTypes.filter(st => ratings[st.code] && ratings[st.code] > 0).length;
  const notRatedSkillCount = Math.max(strokeTypes.length - ratedSkillCount, 0);
  const totalScorePct = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  return (
    <Box>
      {!embeddedInEvaluatorTabs && (
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {pageTitle.main}
        </Typography>
        {pageTitle.level && (
          <Chip 
            label={pageTitle.level} 
            color="primary"
            sx={{ 
              fontWeight: 700,
              fontSize: '0.875rem',
              height: 28,
            }}
          />
        )}
        {isStrokeLikeSubcategory && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                overflow: 'hidden',
                borderRadius: 999,
                border: '1px solid rgba(21, 101, 192, 0.28)',
                boxShadow: '0 8px 18px rgba(21, 101, 192, 0.18)',
                minHeight: 34,
                bgcolor: '#fff',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  px: 1.4,
                  py: 0.55,
                  minWidth: 148,
                  background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
                  color: '#fff',
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, lineHeight: 1 }}>
                  Total Score
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {totalScore}/{maxScore} ({totalScorePct}%)
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.55,
                  minWidth: 86,
                  bgcolor: '#2e7d32',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 900, lineHeight: 1 }}>
                  Rated
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {ratedSkillCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.55,
                  minWidth: 96,
                  bgcolor: '#fff3cd',
                  color: '#7a4f01',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 900, lineHeight: 1 }}>
                  Not Rated
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {notRatedSkillCount}
                </Typography>
              </Box>
            </Box>
            {!hideHeaderSaveDownload && canRate && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSaveAll()}
                disabled={saving || submitting || !selectedPeriodKey || Object.keys(ratings).length === 0 || isSubmitted}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {!hideHeaderSaveDownload && !isPracticalTest && (
              <Tooltip title="Download Manual Form">
                <IconButton
                  size="medium"
                  onClick={handleDownloadManualForm}
                  sx={{ color: '#1565c0', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                >
                  <FileDownloadIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            )}
            {!hideHeaderSaveDownload && isSubmitted && (
              <Tooltip title="Download Report">
                <IconButton
                  size="medium"
                  onClick={handleDownloadReport}
                  sx={{ color: '#c62828', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
                >
                  <PdfIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* View-Only Notice for Players - Hidden for Board Exam and Coach/Academy Evaluation */}
      {isPlayer && !canRate && categoryId !== 'practical-test' && categoryId !== 'coach-academy' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {`You have view-only access for ${categoryLabel}. Only coaches and administrators can edit these evaluations.`}
        </Alert>
      )}

      {/* Period Selection */}
      <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Evaluated By (Players viewing Coach/Academy evaluation) */}
        {shouldShowEvaluatedByDropdown && !embeddedInEvaluatorTabs && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Evaluated By</InputLabel>
            <Select
              value={selectedLinkedCoachId || ''}
              label="Evaluated By"
              onChange={(e) => setSelectedLinkedCoachId(String(e.target.value))}
              disabled={linkedCoachOptions.length === 0}
            >
              {linkedCoachOptions.length === 0 ? (
                <MenuItem value="">
                  <em>No coach linked</em>
                </MenuItem>
              ) : (
                linkedCoachOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </Box>

      

      {/* Bulk Upload Section - Only for Board Exam */}
      {/* NOTE: Bulk upload feature is currently hidden. It requires enhancement to support:
          - Defining stroke code sets and recording order
          - Identifying start/end times for each stroke within a single video
          - See PRD for future implementation details
      */}
      {false && canUploadVideo && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Bulk Video Upload (Multiple Strokes)
          </Typography>
          {!selectedPeriodKey && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please select an Evaluation Period before uploading videos. Videos are associated with the selected period.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select multiple stroke codes and upload videos for each. This allows you to upload videos for a mix of strokes and shots efficiently.
          </Typography>
          
          {bulkUploadRows.map((row, rowIndex) => (
            <Card key={row.id} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Row {rowIndex + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setBulkUploadRows(prev => prev.filter(r => r.id !== row.id));
                  }}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
              
              {/* Stroke Code Search/Select */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Select Stroke Codes:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {row.strokes.map((stroke) => (
                    <Chip
                      key={stroke.code}
                      label={`${stroke.code} (${stroke.strokeType || '-'}/${stroke.shotType || '-'})`}
                      onDelete={() => {
                        setBulkUploadRows(prev => prev.map(r => 
                          r.id === row.id 
                            ? { ...r, selectedCodes: r.selectedCodes.filter(c => c !== stroke.code), strokes: r.strokes.filter(s => s.code !== stroke.code) }
                            : r
                        ));
                      }}
                      size="small"
                    />
                  ))}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setStrokeSelectDialog({
                      open: true,
                      rowId: row.id,
                      searchTerm: ''
                    });
                  }}
                >
                  + Add Stroke Codes
                </Button>
              </Box>
              
              {/* Selected Strokes Display */}
              {row.strokes.length > 0 && (
                <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{strokeLabels.courtPosition}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{strokeLabels.strokeType}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{strokeLabels.shotType}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{strokeLabels.placement}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.strokes.map((stroke) => (
                        <TableRow key={stroke.code}>
                          <TableCell>{stroke.code}</TableCell>
                          <TableCell>{stroke.courtPosition || '-'}</TableCell>
                          <TableCell>{stroke.strokeType || '-'}</TableCell>
                          <TableCell>{stroke.shotType || '-'}</TableCell>
                          <TableCell>{stroke.placement || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* Upload Button */}
              <Box>
                <input
                  accept="video/*"
                  style={{ display: 'none' }}
                  id={`bulk-upload-${row.id}`}
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && row.strokes.length > 0) {
                      setBulkUploadRows(prev => prev.map(r => 
                        r.id === row.id ? { ...r, file } : r
                      ));
                      handleBulkRowUpload(row.id, file, row.strokes);
                    }
                  }}
                  disabled={row.uploading || row.strokes.length === 0 || !selectedPeriodKey}
                />
                <label htmlFor={`bulk-upload-${row.id}`}>
                  <Tooltip title={!selectedPeriodKey ? "Please select an Evaluation Period first" : row.strokes.length === 0 ? "Select stroke codes first" : "Upload video"}>
                    <span>
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={row.uploading || row.strokes.length === 0 || !selectedPeriodKey}
                        size="small"
                      >
                        {row.uploading ? 'Uploading...' : `Upload Video (${row.strokes.length} stroke${row.strokes.length > 1 ? 's' : ''})`}
                      </Button>
                    </span>
                  </Tooltip>
                </label>
                {row.file && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Selected: {row.file.name}
                  </Typography>
                )}
              </Box>
            </Card>
          ))}
          
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setBulkUploadRows(prev => [...prev, {
                id: `row-${Date.now()}-${Math.random()}`,
                selectedCodes: [],
                strokes: [],
                file: null,
                uploading: false,
              }]);
            }}
            sx={{ mt: 1 }}
          >
            Add Row
          </Button>
        </Box>
      )}


      {/* Show content based on subcategory prop */}
      {isStrokeLikeSubcategory && (
      <Box sx={{ py: 2 }}>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((prev) => !prev)}
            disabled={loading && allStrokeTypes.length === 0}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        {showFilters && allStrokeTypes.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{strokeLabels.courtPosition}</InputLabel>
                  <Select
                    value={filterCourtPosition}
                    label={strokeLabels.courtPosition}
                    onChange={(e) => setFilterCourtPosition(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUniqueValues('courtPosition').map((val: string) => (
                      <MenuItem key={val} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{strokeLabels.halfSide}</InputLabel>
                  <Select
                    value={filterHalfSide}
                    label={strokeLabels.halfSide}
                    onChange={(e) => setFilterHalfSide(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUniqueValues('halfSide').map((val: string) => {
                      let displayVal = val;
                      if (!isFootballSport && (val.toLowerCase().includes('right service court') || val.toLowerCase().includes('right side') || val.toLowerCase().includes('right'))) {
                        displayVal = 'Right Side';
                      } else if (!isFootballSport && (val.toLowerCase().includes('left service court') || val.toLowerCase().includes('left side') || val.toLowerCase().includes('left'))) {
                        displayVal = 'Left Side';
                      }
                      return <MenuItem key={val} value={val}>{displayVal}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{strokeLabels.strokeType}</InputLabel>
                  <Select
                    value={filterStrokeType}
                    label={strokeLabels.strokeType}
                    onChange={(e) => setFilterStrokeType(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUniqueValues('strokeType').map((val: string) => (
                      <MenuItem key={val} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{strokeLabels.playType}</InputLabel>
                  <Select
                    value={filterPlayType}
                    label={strokeLabels.playType}
                    onChange={(e) => setFilterPlayType(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUniqueValues('playType').map((val: string) => (
                      <MenuItem key={val} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{strokeLabels.shotType}</InputLabel>
                  <Select
                    value={filterShotType}
                    label={strokeLabels.shotType}
                    onChange={(e) => setFilterShotType(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUniqueValues('shotType').map((val: string) => (
                      <MenuItem key={val} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{strokeLabels.difficulty}</InputLabel>
                  <Select
                    value={filterDifficulty}
                    label={strokeLabels.difficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUniqueValues('difficulty').map((val: string) => (
                      <MenuItem key={val} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rating Status</InputLabel>
                  <Select
                    value={filterRatedStatus}
                    label="Rating Status"
                    onChange={(e) => setFilterRatedStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Skills</MenuItem>
                    <MenuItem value="rated">Rated Only</MenuItem>
                    <MenuItem value="not-rated">Not Rated Only</MenuItem>
                    <MenuItem value="1">Rating: 1 Star</MenuItem>
                    <MenuItem value="2">Rating: 2 Stars</MenuItem>
                    <MenuItem value="3">Rating: 3 Stars</MenuItem>
                    <MenuItem value="4">Rating: 4 Stars</MenuItem>
                    <MenuItem value="5">Rating: 5 Stars</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setFilterCourtPosition('');
                    setFilterHalfSide('');
                    setFilterStrokeType('');
                    setFilterPlayType('');
                    setFilterShotType('');
                    setFilterDifficulty('');
                    setFilterRatedStatus('all');
                  }}
                  sx={{ height: '40px' }}
                >
                  Clear All Filters
                </Button>
              </Grid>
            </Grid>
            {(filterCourtPosition || filterHalfSide || filterStrokeType || filterPlayType || filterShotType || filterDifficulty || filterRatedStatus !== 'all') && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {filterCourtPosition && (
                  <Chip label={`${strokeLabels.courtPosition}: ${filterCourtPosition}`} color="primary" variant="outlined" onDelete={() => setFilterCourtPosition('')} />
                )}
                {filterHalfSide && (
                  <Chip label={`${strokeLabels.halfSide}: ${filterHalfSide}`} color="primary" variant="outlined" onDelete={() => setFilterHalfSide('')} />
                )}
                {filterStrokeType && (
                  <Chip label={`${strokeLabels.strokeType}: ${filterStrokeType}`} color="primary" variant="outlined" onDelete={() => setFilterStrokeType('')} />
                )}
                {filterPlayType && (
                  <Chip label={`${strokeLabels.playType}: ${filterPlayType}`} color="primary" variant="outlined" onDelete={() => setFilterPlayType('')} />
                )}
                {filterShotType && (
                  <Chip label={`${strokeLabels.shotType}: ${filterShotType}`} color="primary" variant="outlined" onDelete={() => setFilterShotType('')} />
                )}
                {filterDifficulty && (
                  <Chip label={`${strokeLabels.difficulty}: ${filterDifficulty} Star${parseInt(filterDifficulty) > 1 ? 's' : ''}`} color="primary" variant="outlined" onDelete={() => setFilterDifficulty('')} />
                )}
                {filterRatedStatus !== 'all' && (
                  <Chip
                    label={
                      filterRatedStatus === 'rated' ? 'Rating: Rated Only' :
                      filterRatedStatus === 'not-rated' ? 'Rating: Not Rated Only' :
                      `Rating: ${filterRatedStatus} Star${parseInt(filterRatedStatus) > 1 ? 's' : ''}`
                    }
                    color="primary"
                    variant="outlined"
                    onDelete={() => setFilterRatedStatus('all')}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {loading || (embeddedInEvaluatorTabs && loadingRatings) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {loading ? 'Loading stroke types…' : 'Loading ratings…'}
            </Typography>
          </Box>
        ) : strokeTypes.length > 0 || allStrokeTypes.length > 0 ? (
          <Box sx={{ display: 'flex', gap: { xs: 1, lg: 0.75 }, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Bulk Rating Controls */}
            {canRate && (() => {
              return (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.25,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'wrap',
                    overflow: 'visible',
                    order: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    Bulk Rating:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5, whiteSpace: 'nowrap' }}>
                    <Checkbox
                      indeterminate={isStrokesIndeterminate()}
                      checked={isAllStrokesSelected()}
                      onChange={(e) => handleSelectAllStrokes(e.target.checked)}
                      disabled={saving || submitting || getFilteredStrokes().length === 0}
                      size="small"
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Select Filtered ({getFilteredStrokes().length})
                    </Typography>
                  </Box>
                  <Rating
                    value={bulkRating}
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        setBulkRating(newValue);
                      }
                    }}
                    size="small"
                    disabled={saving}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyBulkRating}
                    disabled={bulkRating === 0 || selectedStrokes.size === 0 || saving || submitting}
                    sx={{ bgcolor: '#1976d2', whiteSpace: 'nowrap' }}
                  >
                    Apply to {selectedStrokes.size} Selected
                  </Button>
                  {selectedStrokes.size > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedStrokes(new Set())}
                      disabled={saving}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Clear Selection
                    </Button>
                  )}
                </Box>
              );
            })()}

            <Box sx={{ position: 'relative' }}>
              {/* Scroll Buttons (only for table/upload view) */}
              {canUploadVideo && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  zIndex: 1000,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (tableContainerRef.current) {
                      tableContainerRef.current.scrollBy({ top: -100, behavior: 'smooth' });
                    }
                  }}
                  sx={{
                    bgcolor: '#1976d2',
                    boxShadow: 3,
                    color: 'white',
                    '&:hover': { bgcolor: '#1565c0' },
                  }}
                >
                  <ArrowUpIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (tableContainerRef.current) {
                      tableContainerRef.current.scrollBy({ top: 100, behavior: 'smooth' });
                    }
                  }}
                  sx={{
                    bgcolor: '#1976d2',
                    boxShadow: 3,
                    color: 'white',
                    '&:hover': { bgcolor: '#1565c0' },
                  }}
                >
                  <ArrowDownIcon />
                </IconButton>
              </Box>
              )}

            {/* Upload Metrics */}
            {!canUploadVideo && <></>}

            {rankingViewMode === 'accordion' && (
              <Box sx={{ mb: 2 }}>
                <Card
                  sx={{
                    mb: 1,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: '1px solid #90caf9',
                    bgcolor: '#e3f2fd',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#0d47a1',
                      fontWeight: 800,
                      lineHeight: 1.3,
                      display: 'block',
                      whiteSpace: 'normal',
                    }}
                  >
                    {isFootballSport
                      ? (canUploadVideo
                          ? `${strokeLabels.courtPosition} | ${strokeLabels.halfSide} | ${strokeLabels.strokeType} | ${strokeLabels.strokeAction} | ${strokeLabels.shotType} | ${strokeLabels.placement} | Rating / Trend | Details | Upload Video | Review`
                          : `${strokeLabels.courtPosition} | ${strokeLabels.halfSide} | ${strokeLabels.strokeType} | ${strokeLabels.strokeAction} | ${strokeLabels.shotType} | ${strokeLabels.placement} | Rating / Trend | Details`)
                      : (canUploadVideo
                          ? 'Court Position | Half Court | Stroke Type | Stroke Action | Shot Type | Placement | Rating / Trend | Details | Upload Video | Review Comments'
                          : 'Court Position | Half Court | Stroke Type | Stroke Action | Shot Type | Placement | Rating / Trend | Details')}
                  </Typography>
                </Card>
                {loadingRatings ? (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading ratings...
                    </Typography>
                  </Paper>
                ) : (
                  strokeTypes.map((strokeType) => {
                    const isUploaded = uploadedVideos.has(strokeType.code);
                    const trendDirection = getTrendDirectionForStroke(strokeType.code);
                    const apiTrendData = skillTrendByCode[strokeType.code] || [];
                    const previousForCode = Number(savedRatings?.[strokeType.code] || 0);
                    const currentForCode = Number(ratings?.[strokeType.code] || 0);
                    const hasVisibleRating = previousForCode > 0 || currentForCode > 0;
                    const trendData = hasVisibleRating && apiTrendData.length > 1
                      ? apiTrendData
                      : (hasVisibleRating
                        ? [
                            { rating: previousForCode, label: 'Prev', at: '' },
                            { rating: currentForCode, label: 'Now', at: '' },
                          ]
                        : []);
                    const videoAttempts = uploadedVideos.get(strokeType.code);
                    const attempts = videoAttempts ? (Array.isArray(videoAttempts) ? videoAttempts : [videoAttempts]) : [];
                    const sortedAttempts = attempts.sort((a: any, b: any) => (a.uploadAttemptNumber || 1) - (b.uploadAttemptNumber || 1));
                    const isAttemptReviewed = (attemptNum: number) => {
                      const attempt = sortedAttempts.find((a: any) => (a.uploadAttemptNumber || 1) === attemptNum);
                      return !!(attempt && attempt.status && attempt.status !== 'pending');
                    };
                    const getAttempt = (attemptNum: number) => {
                      return sortedAttempts.find((a: any) => (a.uploadAttemptNumber || 1) === attemptNum);
                    };
                    return (
                      <Accordion
                        key={`mobile-${strokeType.id}`}
                        disableGutters
                        expanded={expandedAccordionCode === strokeType.code}
                        onChange={(event, expanded) => handleAccordionChange(event, strokeType.code, expanded)}
                        sx={{
                          mb: 0.5,
                          border: '1px solid #dbe8f6',
                          borderLeft: downgradedCodes.has(strokeType.code)
                            ? '4px solid #ff9800'
                            : isUploaded
                              ? '4px solid #4caf50'
                              : '1px solid #dbe8f6',
                          bgcolor: downgradedCodes.has(strokeType.code) ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            '& .MuiAccordionSummary-content': {
                              minWidth: 0,
                              my: 0.75,
                            },
                          }}
                        >
                          <Box sx={{ width: '100%', minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, mb: 0.75 }}>
                              {canRate ? (
                                <Checkbox
                                  checked={selectedStrokes.has(strokeType.code)}
                                  onChange={(e) => handleSelectStroke(strokeType.code, e.target.checked)}
                                  disabled={saving || submitting}
                                  size="small"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0.35, mt: -0.25, flexShrink: 0 }}
                                />
                              ) : null}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {renderSkillIdentity(strokeType, true)}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                minWidth: 0,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Box sx={{ flexShrink: 0 }}>
                                {renderSkillActionButtons(strokeType)}
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.55,
                                  flexShrink: 0,
                                  ml: 'auto',
                                }}
                              >
                                {canRate ? (
                                  <Rating
                                    value={getDisplayRatingForStroke(strokeType.code)}
                                    onChange={(_, newValue) => handleRatingChange(strokeType.code, newValue)}
                                    max={5}
                                    highlightSelectedOnly={false}
                                    size="small"
                                    disabled={saving || submitting || isPracticalTest}
                                  />
                                ) : (
                                  <Rating value={getDisplayRatingForStroke(strokeType.code)} size="small" max={5} readOnly highlightSelectedOnly={false} />
                                )}
                                {trendDirection === 'up' ? (
                                  <ArrowUpIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                ) : trendDirection === 'down' ? (
                                  <ArrowDownIcon sx={{ color: 'error.main', fontSize: 18 }} />
                                ) : (
                                  <Box sx={{ width: 18 }} />
                                )}
                                <Box sx={{ height: 26, width: { xs: 78, sm: 118 }, minWidth: 76, flexShrink: 0 }}>
                                  {trendData.length > 1 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={trendData}>
                                        <Line type="monotone" dataKey="rating" stroke="#1976d2" strokeWidth={2} dot={false} />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  ) : null}
                                </Box>
                              </Box>
                            </Box>
                            {strokeType.code && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', textAlign: 'right', mt: 0.15, fontSize: '0.62rem', fontWeight: 800 }}
                              >
                                {strokeType.code}
                              </Typography>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                            {canUploadVideo && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}>
                                  Upload Attempts
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {[1, 2, 3].map((attemptNum) => {
                                    const attempt = getAttempt(attemptNum);
                                    const isUploading = uploadingStrokes.has(`${strokeType.code}-${attemptNum}`);
                                    let showUploadButton = false;
                                    if (attemptNum === 1) {
                                      showUploadButton = !attempt;
                                    } else if (attemptNum === 2) {
                                      showUploadButton = !attempt && isAttemptReviewed(1);
                                    } else if (attemptNum === 3) {
                                      showUploadButton = !attempt && isAttemptReviewed(2);
                                    }

                                    if (attempt) {
                                      const status = attempt?.status || 'pending';
                                      const statusLabel = status === 'validated' || status === 'approved' || status === 'verified' ? 'Verified'
                                        : status === 'rejected' ? 'Rejected'
                                        : 'Pending';
                                      const statusColor = status === 'validated' || status === 'approved' || status === 'verified' ? 'success'
                                        : status === 'rejected' ? 'error'
                                        : 'default';

                                      return (
                                        <Box key={`mobile-attempt-${strokeType.code}-${attemptNum}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                          <Typography variant="caption" sx={{ minWidth: 70, fontWeight: 600 }}>
                                            Attempt {attemptNum}:
                                          </Typography>
                                          <Chip label={statusLabel} color={statusColor as any} size="small" sx={{ height: 22, fontSize: '0.72rem' }} />
                                          {attempt?.videoUrl && (
                                            <Tooltip title="View Video">
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => setViewingVideo({ url: attempt.videoUrl, filename: attempt.filename })}
                                                sx={{ p: 0.3 }}
                                              >
                                                <VisibilityIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      );
                                    }

                                    return (
                                      <Box key={`mobile-upload-${strokeType.code}-${attemptNum}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" sx={{ minWidth: 70, fontWeight: 600 }}>
                                          Attempt {attemptNum}:
                                        </Typography>
                                        {showUploadButton ? (
                                          <>
                                            <input
                                              id={`upload-mobile-${strokeType.code}-${attemptNum}`}
                                              type="file"
                                              accept="video/*"
                                              style={{ display: 'none' }}
                                              disabled={isUploading || saving || submitting}
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                  handleStrokeVideoUpload(strokeType.code, strokeType, file, attemptNum);
                                                }
                                              }}
                                            />
                                            <label htmlFor={`upload-mobile-${strokeType.code}-${attemptNum}`}>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                component="span"
                                                disabled={isUploading || saving || submitting}
                                              >
                                                {isUploading ? 'Uploading...' : `Upload #${attemptNum}`}
                                              </Button>
                                            </label>
                                          </>
                                        ) : (
                                          <Typography variant="caption" color="text.secondary">
                                            Not available yet
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </Box>
            )}

            <Box sx={{ display: !embeddedInEvaluatorTabs && rankingViewMode !== 'accordion' && canUploadVideo && !isMobileOrTablet ? 'block' : 'none' }}>
            <TableContainer 
            ref={tableContainerRef}
            component={Paper} 
            sx={{ 
              boxShadow: 3,
              border: '4px solid #2196f3',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: 'calc(100vh - 400px)',
              minHeight: '600px',
              width: '100%',
              maxWidth: '100%',
              // Keep table inside available content width (prevents overlap with right panel)
              '@media (min-width: 1200px)': {
                width: '100%',
                maxWidth: '100%',
              },
            }}
          >
            <Table stickyHeader sx={{ borderCollapse: 'separate' }}>
              <TableHead>
                <TableRow 
                  sx={{ 
                    background: '#f1f8e9',
                    backgroundImage: `
                      repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(76, 175, 80, 0.2) 3px, rgba(76, 175, 80, 0.2) 4px)
                    `,
                    borderBottom: '4px solid #2196f3',
                    '& .MuiTableCell-root': {
                      borderBottom: '4px solid #2196f3',
                      borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: '#f1f8e9',
                    },
                  }}
                >
                  {canRate && (
                    <TableCell padding="checkbox" sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', width: '48px' }}>
                      <Checkbox
                        indeterminate={isStrokesIndeterminate()}
                        checked={isAllStrokesSelected()}
                        onChange={(e) => handleSelectAllStrokes(e.target.checked)}
                        disabled={saving}
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.25, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', minWidth: 240 }}>Skill</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>
                    {canRate ? 'Your Rating' : 'Rating'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.25, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', minWidth: 180 }}>Details</TableCell>
                  {canUploadVideo && (
                    <>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.25, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', minWidth: 160, width: 180 }}>Upload Video</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.25, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', minWidth: 130, width: 150 }}>Review Comments</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRatings ? (
                  <TableRow>
                    <TableCell colSpan={canUploadVideo ? (canRate ? 6 : 5) : (canRate ? 4 : 3)} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading ratings...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  strokeTypes.map((strokeType) => {
                  // Check if video is uploaded
                  const isUploaded = uploadedVideos.has(strokeType.code);
                  const rowTrendDirection = getTrendDirectionForStroke(strokeType.code);
                  const apiRowTrendData = skillTrendByCode[strokeType.code] || [];
                  const previousForCode = Number(savedRatings?.[strokeType.code] || 0);
                  const currentForCode = Number(ratings?.[strokeType.code] || 0);
                  const hasVisibleRating = previousForCode > 0 || currentForCode > 0;
                  const rowTrendData = hasVisibleRating && apiRowTrendData.length > 1
                    ? apiRowTrendData
                    : (hasVisibleRating
                      ? [
                          { rating: previousForCode, label: 'Prev', at: '' },
                          { rating: currentForCode, label: 'Now', at: '' },
                        ]
                      : []);
                  
                  return (
                    <TableRow 
                      key={strokeType.id} 
                      hover
                      sx={{
                        borderBottom: '1px solid rgba(33, 150, 243, 0.7)',
                        backgroundColor: downgradedCodes.has(strokeType.code)
                          ? 'rgba(255, 152, 0, 0.10)'
                          : isUploaded
                            ? 'rgba(76, 175, 80, 0.08)'
                            : 'transparent',
                        borderLeft: downgradedCodes.has(strokeType.code)
                          ? '4px solid #ff9800'
                          : isUploaded
                            ? '4px solid #4caf50'
                            : 'none',
                        '&:hover': {
                          backgroundColor: downgradedCodes.has(strokeType.code)
                            ? 'rgba(255, 152, 0, 0.16)'
                            : isUploaded
                              ? 'rgba(76, 175, 80, 0.15)'
                              : 'rgba(33, 150, 243, 0.1)',
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid rgba(33, 150, 243, 0.7)',
                          borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                          '&:last-child': {
                            borderRight: 'none',
                          },
                        },
                      }}
                    >
                      {canRate && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedStrokes.has(strokeType.code)}
                            onChange={(e) => handleSelectStroke(strokeType.code, e.target.checked)}
                            disabled={saving || submitting}
                            size="small"
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ verticalAlign: 'middle', minWidth: rightPanelCollapsed ? 520 : 360, maxWidth: rightPanelCollapsed ? 720 : 520 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            {renderSkillIdentity(strokeType)}
                            {strokeType.code && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', textAlign: 'right', mt: 0.15, fontSize: '0.62rem', fontWeight: 800 }}
                              >
                                {strokeType.code}
                              </Typography>
                            )}
                          </Box>
                          {isUploaded && (
                            <Tooltip title="Video uploaded successfully">
                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem', flexShrink: 0 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle', minWidth: 230 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                          {canRate ? (
                            <Rating
                              value={currentForCode}
                              onChange={(_, newValue) => {
                                handleRatingChange(strokeType.code, newValue);
                              }}
                              max={5}
                              highlightSelectedOnly={false}
                              size="small"
                              disabled={saving || submitting || isPracticalTest}
                            />
                          ) : (
                            <Rating
                              value={currentForCode}
                              max={5}
                              highlightSelectedOnly={false}
                              size="small"
                              readOnly
                            />
                          )}
                          {rowTrendDirection === 'up' ? (
                            <ArrowUpIcon sx={{ color: 'success.main', fontSize: 18 }} />
                          ) : rowTrendDirection === 'down' ? (
                            <ArrowDownIcon sx={{ color: 'error.main', fontSize: 18 }} />
                          ) : (
                            <Box sx={{ width: 18 }} />
                          )}
                          <Box sx={{ width: 96, height: 26, flexShrink: 0 }}>
                            {rowTrendData.length > 1 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={rowTrendData}>
                                  <Line type="monotone" dataKey="rating" stroke="#1976d2" strokeWidth={2} dot={false} />
                                </LineChart>
                              </ResponsiveContainer>
                            ) : null}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {renderSkillActionButtons(strokeType)}
                        </Box>
                      </TableCell>
                      {canUploadVideo && (
                        <TableCell sx={{ minWidth: '200px', width: '200px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          {(() => {
                            const videoAttempts = uploadedVideos.get(strokeType.code);
                            const attempts = videoAttempts ? (Array.isArray(videoAttempts) ? videoAttempts : [videoAttempts]) : [];
                            // Sort by attempt number
                            const sortedAttempts = attempts.sort((a: any, b: any) => (a.uploadAttemptNumber || 1) - (b.uploadAttemptNumber || 1));
                            
                            // Helper function to check if an attempt has been reviewed
                            const isAttemptReviewed = (attemptNum: number) => {
                              const attempt = sortedAttempts.find((a: any) => (a.uploadAttemptNumber || 1) === attemptNum);
                              return attempt && attempt.status && attempt.status !== 'pending';
                            };
                            
                            // Helper function to get attempt by number
                            const getAttempt = (attemptNum: number) => {
                              return sortedAttempts.find((a: any) => (a.uploadAttemptNumber || 1) === attemptNum);
                            };
                            
                            // Render attempts 1, 2, 3
                            return [1, 2, 3].map((attemptNum) => {
                              const attempt = getAttempt(attemptNum);
                              const isUploading = uploadingStrokes.has(`${strokeType.code}-${attemptNum}`);
                              
                              // Determine if upload button should be shown
                              let showUploadButton = false;
                              if (attemptNum === 1) {
                                // Always show upload button for attempt 1 if it doesn't exist
                                showUploadButton = !attempt;
                              } else if (attemptNum === 2) {
                                // Show upload button for attempt 2 only if attempt 1 exists and is reviewed
                                showUploadButton = !attempt && isAttemptReviewed(1);
                              } else if (attemptNum === 3) {
                                // Show upload button for attempt 3 only if attempt 2 exists and is reviewed
                                showUploadButton = !attempt && isAttemptReviewed(2);
                              }
                              
                              // If attempt exists, show its status
                              if (attempt) {
                                const status = attempt?.status || 'pending';
                                const statusLabel = status === 'validated' || status === 'approved' || status === 'verified' ? 'Verified' : 
                                                   status === 'rejected' ? 'Rejected' :
                                                   'Pending';
                                const statusColor = status === 'validated' || status === 'approved' || status === 'verified' ? 'success' :
                                                   status === 'rejected' ? 'error' :
                                                   'default';
                                
                                return (
                                  <Box key={`attempt-${attemptNum}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontSize: '0.75rem', lineHeight: '1.2' }}>
                                    <Typography variant="caption" sx={{ minWidth: '65px', fontSize: '0.75rem', fontWeight: 500 }}>
                                      Attempt {attemptNum}:
                                    </Typography>
                                    <Chip label={statusLabel} color={statusColor} size="small" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                    {attempt?.videoUrl ? (
                                      <>
                                        <Tooltip title="View Video">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => setViewingVideo({
                                              url: attempt.videoUrl,
                                              filename: attempt.filename
                                            })}
                                            sx={{ p: 0.25, minWidth: '24px', width: '24px', height: '24px' }}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        {strokeType?.referenceVideos && String(strokeType.referenceVideos).trim().length > 0 && (() => {
                                          const refStr = String(strokeType.referenceVideos || '');
                                          const firstRef = refStr.split(',').map((u: string) => u.trim()).filter((u: string) => u.length > 0)[0];
                                          return firstRef ? (
                                            <Tooltip title="Compare with reference (side-by-side, slow motion)">
                                              <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => setCompareVideos({
                                                  referenceUrl: firstRef,
                                                  playerUrl: attempt.videoUrl,
                                                  referenceLabel: 'Reference',
                                                  playerLabel: attempt.filename || 'Your video',
                                                })}
                                                sx={{ p: 0.25, minWidth: '24px', width: '24px', height: '24px' }}
                                              >
                                                <CompareArrowsIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          ) : null;
                                        })()}
                                      </>
                                    ) : null}
                                  </Box>
                                );
                              } else if (showUploadButton) {
                                // Show upload button for this attempt
                                return (
                                  <Box key={`attempt-${attemptNum}`} sx={{ mb: 0.5 }}>
                                    <input
                                      accept="video/*"
                                      style={{ display: 'none' }}
                                      id={`stroke-upload-${strokeType.code}-${attemptNum}`}
                                      type="file"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          handleStrokeVideoUpload(strokeType.code, strokeType, file, attemptNum);
                                        }
                                      }}
                                      disabled={isUploading}
                                    />
                                    <label htmlFor={`stroke-upload-${strokeType.code}-${attemptNum}`}>
                                      <Tooltip title={`Upload video for Attempt ${attemptNum}`}>
                                        <Button
                                          component="span"
                                          size="small"
                                          variant="outlined"
                                          startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                          disabled={isUploading}
                                          sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                                        >
                                          Upload Attempt {attemptNum}
                                        </Button>
                                      </Tooltip>
                                    </label>
                                  </Box>
                                );
                              }
                              return null;
                            }).filter(Boolean);
                          })()}
                        </TableCell>
                      )}
                      {canUploadVideo && (
                        <TableCell sx={{ minWidth: '150px', width: '150px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          {(() => {
                            const videoAttempts = uploadedVideos.get(strokeType.code);
                            const attempts = videoAttempts ? (Array.isArray(videoAttempts) ? videoAttempts : [videoAttempts]) : [];
                            // Sort by attempt number
                            const sortedAttempts = attempts.sort((a: any, b: any) => (a.uploadAttemptNumber || 1) - (b.uploadAttemptNumber || 1));
                            
                            // Helper function to get attempt by number
                            const getAttempt = (attemptNum: number) => {
                              return sortedAttempts.find((a: any) => (a.uploadAttemptNumber || 1) === attemptNum);
                            };
                            
                            // Render review comments for attempts 1, 2, 3
                            const reviewLinks = [1, 2, 3].map((attemptNum) => {
                              const attempt = getAttempt(attemptNum);
                              const hasComments = attempt?.adminFeedback && attempt.adminFeedback.trim();
                              
                              if (hasComments) {
                                return (
                                  <Box key={`review-${attemptNum}`} sx={{ mb: 0.5, lineHeight: '1.2' }}>
                                    <Tooltip 
                                      title={attempt.adminFeedback}
                                      arrow
                                      placement="top"
                                    >
                                      <Link
                                        component="button"
                                        variant="caption"
                                        sx={{ 
                                          fontSize: '0.75rem',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                          color: 'primary.main'
                                        }}
                                      >
                                        {attemptNum}
                                      </Link>
                                    </Tooltip>
                                  </Box>
                                );
                              }
                              return null;
                            }).filter(Boolean);
                            
                            // If no review links, show dash
                            if (reviewLinks.length === 0) {
                              return (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  -
                                </Typography>
                              );
                            }
                            
                            return reviewLinks;
                          })()}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
            </Box>

          {/* Evaluation History - Removed per requirements */}
          {false && evaluationHistory.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Evaluation History
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Total Score</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Backcourt %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Midcourt %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Frontcourt %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluationHistory.map((history, idx) => {
                      // Calculate coverage for this history entry
                      const backcourtEntries = history.entries.filter((e: any) => 
                        (e.courtPosition || '').toLowerCase().includes('backcourt') || 
                        (e.courtPosition || '').toLowerCase().includes('back court')
                      );
                      const midcourtEntries = history.entries.filter((e: any) => 
                        (e.courtPosition || '').toLowerCase().includes('midcourt') || 
                        (e.courtPosition || '').toLowerCase().includes('mid court')
                      );
                      const frontcourtEntries = history.entries.filter((e: any) => 
                        (e.courtPosition || '').toLowerCase().includes('frontcourt') || 
                        (e.courtPosition || '').toLowerCase().includes('front court')
                      );
                      
                      const backcourtTotal = backcourtEntries.reduce((sum: number, e: any) => sum + (e.rating || 0), 0);
                      const backcourtMax = backcourtEntries.length * 5;
                      const midcourtTotal = midcourtEntries.reduce((sum: number, e: any) => sum + (e.rating || 0), 0);
                      const midcourtMax = midcourtEntries.length * 5;
                      const frontcourtTotal = frontcourtEntries.reduce((sum: number, e: any) => sum + (e.rating || 0), 0);
                      const frontcourtMax = frontcourtEntries.length * 5;

                      return (
                        <TableRow 
                          key={idx}
                          hover
                          onClick={() => {
                            const historyDate = new Date(history.date);
                            const year = historyDate.getFullYear();
                            const month = historyDate.getMonth() + 1;
                            const periodKey = periodType === 'yearly' ? `Y-${year}` : periodType === 'half-yearly' ? (month <= 6 ? `H1-${year}` : `H2-${year}`) : `Q${Math.ceil(month / 3)}-${year}`;
                            setSelectedPeriodKey(periodKey);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{new Date(history.date).toLocaleDateString()}</TableCell>
                          <TableCell>{history.totalScore} / {history.maxScore}</TableCell>
                          <TableCell>{backcourtMax > 0 ? ((backcourtTotal / backcourtMax) * 100).toFixed(1) : 0}%</TableCell>
                          <TableCell>{midcourtMax > 0 ? ((midcourtTotal / midcourtMax) * 100).toFixed(1) : 0}%</TableCell>
                          <TableCell>{frontcourtMax > 0 ? ((frontcourtTotal / frontcourtMax) * 100).toFixed(1) : 0}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          </Box>
          {isStrokeLikeSubcategory && (
            <Box
              sx={{
                width: rightPanelCollapsed ? 52 : 280,
                flexShrink: 0,
                display: { xs: 'none', lg: 'block' },
                position: 'sticky',
                top: 12,
                mt: 7,
                alignSelf: 'flex-start',
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                pr: 0,
                transition: 'width 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: rightPanelCollapsed ? 'center' : 'space-between', alignItems: 'center', mb: 1 }}>
                {!rightPanelCollapsed && (
                  <Typography variant="caption" sx={{ fontWeight: 900, color: '#0f4c81' }}>
                    Performance Panel
                  </Typography>
                )}
                <Tooltip title={rightPanelCollapsed ? 'Show performance panel' : 'Shrink performance panel'}>
                  {rightPanelCollapsed ? (
                    <IconButton
                      size="small"
                      onClick={() => setRightPanelCollapsed(false)}
                      sx={{
                        border: '2px solid #1976d2',
                        bgcolor: '#e3f2fd',
                        color: '#0d47a1',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.18)',
                        '&:hover': { bgcolor: '#bbdefb' },
                        '& .MuiSvgIcon-root': { fontWeight: 900, strokeWidth: 1.4, stroke: 'currentColor' },
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => setRightPanelCollapsed(true)}
                      sx={{
                        border: '2px solid #1976d2',
                        bgcolor: '#e3f2fd',
                        color: '#0d47a1',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.18)',
                        '&:hover': { bgcolor: '#bbdefb' },
                        '& .MuiSvgIcon-root': { fontWeight: 900, strokeWidth: 1.4, stroke: 'currentColor' },
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  )}
                </Tooltip>
              </Box>
              {rightPanelCollapsed ? (
                <Box
                  sx={{
                    border: '1px solid #dbe8f6',
                    borderRadius: 2,
                    bgcolor: '#fbfdff',
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 260,
                  }}
                >
                  <Stack spacing={1} alignItems="center">
                    {['Evaluation Performance', 'Rating Reference', 'Performance Feedback'].map((label) => (
                      <Tooltip title={label} placement="left" key={label}>
                        <Box
                          sx={{
                            width: 34,
                            minHeight: 92,
                            borderRadius: 1.5,
                            border: '1px solid #bfdbfe',
                            bgcolor: '#e3f2fd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              writingMode: 'vertical-rl',
                              transform: 'rotate(180deg)',
                              fontWeight: 900,
                              color: '#0f4c81',
                              letterSpacing: 0.2,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              ) : (
              <>
              <Accordion defaultExpanded sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Evaluation Performance</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Total Score</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{totalScore}/{maxScore}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={maxScore > 0 ? (totalScore / maxScore) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Rated Score</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#f57c00' }}>{ratedScore}/{ratedMaxScore}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={ratedMaxScore > 0 ? (ratedScore / ratedMaxScore) * 100 : 0} color="warning" sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Backcourt</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#f57c00' }}>{backcourtScore}/{backcourtMax}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={backcourtMax > 0 ? (backcourtScore / backcourtMax) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Midcourt</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{midcourtScore}/{midcourtMax}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={midcourtMax > 0 ? (midcourtScore / midcourtMax) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Frontcourt</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#388e3c' }}>{frontcourtScore}/{frontcourtMax}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={frontcourtMax > 0 ? (frontcourtScore / frontcourtMax) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Rating Reference</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label="1" size="small" sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 700, minWidth: 28 }} />
                      <Typography
                        variant="caption"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {isFootballSport ? 'Hit target zones 0-10%' : 'Inconsistent contact and ready stance'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label="2" size="small" sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 700, minWidth: 28 }} />
                      <Typography
                        variant="caption"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {isFootballSport ? 'Hit target zones 11-25%' : '1-3 shots with proper grip/action'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label="3" size="small" sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 700, minWidth: 28 }} />
                      <Typography
                        variant="caption"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {isFootballSport ? 'Hit target zones 30-50%' : '4-10 shot rally with control'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label="4" size="small" sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700, minWidth: 28 }} />
                      <Typography
                        variant="caption"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {isFootballSport ? 'Hit target zones 51-70%' : '10+ rallies with pace and accuracy'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label="5" size="small" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 700, minWidth: 28 }} />
                      <Typography
                        variant="caption"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {isFootballSport ? 'Hit target zones 70+%' : '15+ rallies with speed and tactics'}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Performance Feedback</Typography>
                </AccordionSummary>
                <AccordionDetails>
                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700 }}>
                  {feedbackForPanel.daysSincePreviousSave === null
                    ? 'First saved baseline'
                    : `Compared with previous save: ${feedbackForPanel.daysSincePreviousSave} day(s)`}
                </Typography>
                {feedbackForPanel.currentSavedAt && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                    Current save: {new Date(feedbackForPanel.currentSavedAt).toLocaleDateString()}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700 }}>
                  Increased: {feedbackForPanel.increased}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
                  No Changes: {feedbackForPanel.same}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 700, mb: 1 }}>
                  Decreased: {feedbackForPanel.decreased}
                </Typography>
                {feedbackForPanel.decreased > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {feedbackForPanel.decreased} skill rating(s) were downgraded. Highlighted rows are marked in amber.
                  </Alert>
                )}
                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700 }}>
                  Improvement Rate vs Last Save: {feedbackForPanel.improvementRatePct.toFixed(1)}%
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, mb: 1 }}>
                  Total Score: {feedbackForPanel.currentTotalScore}/{feedbackForPanel.maxPossibleScore} ({feedbackForPanel.totalScorePct.toFixed(1)}%)
                </Typography>
                {(() => {
                  const chartData = feedbackForPanel.totalScoreTrendData.length === 1
                    ? [
                        { ...feedbackForPanel.totalScoreTrendData[0], label: 'Start' },
                        { ...feedbackForPanel.totalScoreTrendData[0], label: 'Now' },
                      ]
                    : feedbackForPanel.totalScoreTrendData;
                  return chartData.length > 0 ? (
                  <Box sx={{ width: '100%', height: 120, mb: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <Line type="monotone" dataKey="scorePct" stroke="#1976d2" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                  ) : null;
                })()}
                {feedbackForPanel.improvedItems.length > 0 ? (
                  <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                      Improved Skills
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {feedbackForPanel.improvedItems.map((item: any) => (
                        <Box component="li" key={item.code} sx={{ listStyleType: 'disc' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ color: 'success.main', fontSize: 14 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
                              {item.label}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
                            {item.previous} {'->'} {item.current}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No improved skills in the last save.
                  </Typography>
                )}
                {feedbackForPanel.changedItems?.some((item: any) => item.direction === 'down') && (
                  <Box sx={{ mt: 1, maxHeight: 160, overflowY: 'auto' }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5, color: 'warning.main' }}>
                      Downgraded Skills
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {feedbackForPanel.changedItems
                        .filter((item: any) => item.direction === 'down')
                        .map((item: any) => (
                          <Box component="li" key={`down-${item.code}`} sx={{ listStyleType: 'disc' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark' }}>
                              {item.label}: {item.previous} {'->'} {item.current}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                )}
                </AccordionDetails>
              </Accordion>
              </>
              )}
            </Box>
          )}
          </Box>
        ) : allStrokeTypes.length > 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              No stroke types match the selected filters.
            </Alert>
            <Button
              variant="outlined"
              onClick={() => {
                setFilterCourtPosition('');
                setFilterHalfSide('');
                setFilterStrokeType('');
                setFilterPlayType('');
                setFilterShotType('');
                setFilterDifficulty('');
              }}
            >
              Clear Filters
            </Button>
          </Box>
        ) : (
          <Box sx={{ py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              No stroke types available. Please check your filters or contact support.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Debug: selectedSubcategory={selectedSubcategory}, loading={String(loading)}, strokeTypes={strokeTypes.length}, allStrokeTypes={allStrokeTypes.length}, selectedSport={selectedSport}
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {/* Fallback if subcategory doesn't match */}
      {!isStrokeLikeSubcategory && selectedSubcategory !== 'drills' && selectedSubcategory !== 'fitness' && (
        <Alert severity="warning">
          Unknown subcategory: {selectedSubcategory}. Please select a valid subcategory.
        </Alert>
      )}

      {selectedSubcategory === 'drills' && (
        <Box sx={{ py: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : drills.length > 0 ? (
            <Box>
              <Alert severity="info">
                Drills evaluation is coming soon. This will allow you to evaluate drills performance for ranking.
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Total drills available: {drills.length} (Level: {selectedSkillLevel})
              </Typography>
              {/* TODO: Implement drills evaluation table similar to strokes */}
            </Box>
          ) : (
            <Alert severity="info">No drills available for {selectedSkillLevel} level</Alert>
          )}
        </Box>
      )}

      {selectedSubcategory === 'fitness' && (
        <Box sx={{ py: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : fitness.length > 0 ? (
            <Box>
              <Alert severity="info">
                Fitness evaluation is coming soon. This will allow you to evaluate fitness benchmarks for ranking.
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Total fitness benchmarks available: {fitness.length} (Level: {selectedSkillLevel})
              </Typography>
              {/* TODO: Implement fitness evaluation table similar to strokes */}
            </Box>
          ) : (
            <Alert severity="info">No fitness benchmarks available for {selectedSkillLevel} level</Alert>
          )}
        </Box>
      )}

      {(() => {
        const titleStructuredGroups = viewingSkillDetails ? getStructuredLearningGroups(viewingSkillDetails) : [];
        const titleAdditionalGroups = viewingSkillDetails ? getAdditionalVideoGroups(viewingSkillDetails) : [];
        const titleSponsors = getDistinctSponsors([
          ...titleStructuredGroups.flatMap((group) => group.rows),
          ...titleAdditionalGroups.flatMap((group) => group.rows),
        ]).slice(0, 4);

        return (
      <Dialog
        open={!!viewingSkillDetails}
        onClose={() => setViewingSkillDetails(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            background: 'linear-gradient(90deg, #e3f2fd 0%, #e8f5e9 48%, #fff3e0 100%)',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip label={viewingSkillDetails?.code || 'Skill'} color="primary" size="small" sx={{ fontWeight: 900 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                Training Videos
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#102a43', lineHeight: 1.25 }}>
                {getSkillIdentityLine(viewingSkillDetails)}
              </Typography>
              {titleSponsors.map((sponsor, index) => (
                sponsor.sponsorLogoUrl ? (
                  <Box
                    key={`title-sponsor-${sponsor.sponsorLogoUrl}-${index}`}
                    component="img"
                    src={sponsor.sponsorLogoUrl}
                    alt={sponsor.sponsorName || 'Sponsor'}
                    sx={{ width: 78, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: '#fff', border: '1px solid #e0e7ef', p: 0.5 }}
                  />
                ) : null
              ))}
            </Box>
          </Box>
          <IconButton onClick={() => setViewingSkillDetails(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderSkillDetailsDialogContent(viewingSkillDetails)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingSkillDetails(null)}>Close</Button>
        </DialogActions>
      </Dialog>
        );
      })()}

      <Dialog
        open={!!viewingCoachFeedback}
        onClose={() => setViewingCoachFeedback(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            background: 'linear-gradient(90deg, #fff8e1 0%, #fce4ec 100%)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip label={viewingCoachFeedback?.code || 'Skill'} color="warning" size="small" sx={{ fontWeight: 900 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                Coach Feedback
              </Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#5d4037', lineHeight: 1.25 }}>
              {getSkillIdentityLine(viewingCoachFeedback)}
            </Typography>
          </Box>
          <IconButton onClick={() => setViewingCoachFeedback(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderCoachFeedbackDialogContent(viewingCoachFeedback)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingCoachFeedback(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Floating video player (bottom-right, plays immediately, max/min/close) */}
      <FloatingVideoPlayer
        open={!!viewingVideo}
        url={viewingVideo?.url ?? ''}
        title={viewingVideo?.filename || 'Video'}
        onClose={() => setViewingVideo(null)}
      />

      {compareVideos && (
        <SideBySideVideoPlayer
          referenceVideoUrl={compareVideos.referenceUrl}
          playerVideoUrl={compareVideos.playerUrl}
          referenceLabel={compareVideos.referenceLabel || 'Reference'}
          playerLabel={compareVideos.playerLabel || 'Your video'}
          onClose={() => setCompareVideos(null)}
        />
      )}

      {/* Stroke Code Selection Dialog for Bulk Upload */}
      <Dialog
        open={strokeSelectDialog.open}
        onClose={() => setStrokeSelectDialog({ open: false, rowId: null, searchTerm: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Stroke Codes</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search stroke code, stroke type, or shot type"
            value={strokeSelectDialog.searchTerm}
            onChange={(e) => setStrokeSelectDialog(prev => ({ ...prev, searchTerm: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
            placeholder="Leave empty to see all"
          />
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {(() => {
              const filtered = allStrokeTypes.filter(st => 
                !strokeSelectDialog.searchTerm || 
                st.code.toLowerCase().includes(strokeSelectDialog.searchTerm.toLowerCase()) ||
                (st.strokeType && st.strokeType.toLowerCase().includes(strokeSelectDialog.searchTerm.toLowerCase())) ||
                (st.shotType && st.shotType.toLowerCase().includes(strokeSelectDialog.searchTerm.toLowerCase()))
              );
              
              if (filtered.length === 0) {
                return <Typography variant="body2" color="text.secondary">No strokes found matching your search.</Typography>;
              }
              
              return (
                <List>
                  {filtered.slice(0, 50).map((stroke) => {
                    const row = bulkUploadRows.find(r => r.id === strokeSelectDialog.rowId);
                    const isSelected = row?.strokes.some(s => s.code === stroke.code);
                    return (
                      <ListItem
                        key={stroke.code}
                        disablePadding
                      >
                        <ListItemButton
                          onClick={() => {
                            if (strokeSelectDialog.rowId) {
                              setBulkUploadRows(prev => prev.map(r => 
                                r.id === strokeSelectDialog.rowId 
                                  ? { 
                                      ...r, 
                                      selectedCodes: isSelected 
                                        ? r.selectedCodes.filter(c => c !== stroke.code)
                                        : [...new Set([...r.selectedCodes, stroke.code])],
                                      strokes: isSelected
                                        ? r.strokes.filter(s => s.code !== stroke.code)
                                        : [...r.strokes, stroke].filter((s, idx, self) => 
                                            self.findIndex(st => st.code === s.code) === idx
                                          )
                                    }
                                  : r
                              ));
                            }
                          }}
                          selected={isSelected}
                        >
                          <ListItemText
                            primary={stroke.code}
                            secondary={`${stroke.strokeType || '-'} / ${stroke.shotType || '-'} / ${stroke.courtPosition || '-'}`}
                          />
                          {isSelected && <CheckCircleIcon color="primary" sx={{ ml: 1 }} />}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStrokeSelectDialog({ open: false, rowId: null, searchTerm: '' })}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => setStrokeSelectDialog({ open: false, rowId: null, searchTerm: '' })}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default RankingEvaluationWithTabs;

